import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../hooks/useApi';
import { formatDate, formatDateTime, formatTime, formatMoney, currencySymbol } from '../../utils/format';
import SowSelect from '../shared/SowSelect';
import PoSelect from '../shared/PoSelect';
import ContractSelect from '../shared/ContractSelect';
import { useToast } from '../ui/Toast';
import Modal from '../shared/Modal';

// ── WHEN IS AN INVOICE FINISHED? ─────────────────────────────────────────────
//
// "Everything goes to Sage until specifically excluded with a reason, and the
// invoice processing is not complete until the Sage confirmation acceptance has
// been obtained."
//
// The screen did not agree. "Logged" was treated as finished — the attachment
// chip went green and the checklist said "all done" — while the invoice sat there
// having never reached the ledger, which is the step the whole exercise exists
// for. One definition, used everywhere, so a green tick means the same thing on
// every part of this page.
const sageState = (inv) =>
  inv.sage_invoice_id            ? 'in_sage'
  : inv.sage_posted_unidentified ? 'in_sage_unlinked'
  : inv.autoentry_sent_at        ? 'autoentry'
  : inv.sage_excluded_reason     ? 'excluded'
  : inv.status === 'rejected'    ? 'excluded'
  :                                'outstanding';

const isSettled = (inv) => sageState(inv) !== 'outstanding';

// ── Supplier combobox: search existing + inline-create with duplicate warning ─
function SupplierCombobox({ suppliers, value, onChange, onCreated, className }) {
  const [query, setQuery]       = useState('');
  const [open, setOpen]         = useState(false);
  const [creating, setCreating] = useState(false);
  const wrapRef = useRef();

  useEffect(() => {
    const h = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const selected   = suppliers.find(s => s.id === value);
  const trimmed    = query.trim();
  const filtered   = trimmed
    ? suppliers.filter(s => s.name.toLowerCase().includes(trimmed.toLowerCase()))
    : suppliers;
  const exactMatch = suppliers.find(s => s.name.toLowerCase() === trimmed.toLowerCase());
  const similars   = trimmed.length > 3
    ? suppliers.filter(s => {
        const sl = s.name.toLowerCase(), ql = trimmed.toLowerCase();
        return (sl.includes(ql.slice(0,5)) || ql.includes(sl.slice(0,5))) && sl !== ql;
      }).slice(0, 2)
    : [];

  const handleSelect = s => { onChange(s.id); setQuery(''); setOpen(false); };
  const handleClear  = () => { onChange(''); setQuery(''); setOpen(false); };
  const handleCreate = async () => {
    if (!trimmed || creating) return;
    setCreating(true);
    try {
      const { id } = await api.post('suppliers', { name: trimmed });
      const ns = { id, name: trimmed };
      onCreated(ns);
      onChange(id);
      setQuery(''); setOpen(false);
    } catch { /* silent */ } finally { setCreating(false); }
  };

  return (
    <div ref={wrapRef} className="relative">
      <input
        className={className}
        value={open ? query : (selected?.name || '')}
        onChange={e => { setQuery(e.target.value); onChange(''); setOpen(true); }}
        onFocus={() => { setQuery(selected?.name || ''); setOpen(true); }}
        placeholder="Search or create supplier…"
      />
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-0.5 bg-white border border-gray-200 rounded shadow-lg max-h-48 overflow-y-auto text-[9px]">
          <button className="w-full text-left px-2 py-1 text-gray-400 hover:bg-gray-50 border-b border-gray-100" onClick={handleClear}>
            — none —
          </button>
          {filtered.map(s => (
            <button key={s.id} className="w-full text-left px-2 py-1 hover:bg-blue-50 text-gray-700" onClick={() => handleSelect(s)}>
              {s.name}
            </button>
          ))}
          {filtered.length === 0 && !trimmed && (
            <div className="px-2 py-1 text-gray-400 italic">Type to search…</div>
          )}
          {trimmed && !exactMatch && (
            <>
              {similars.length > 0 && (
                <div className="px-2 py-1 bg-amber-50 text-amber-700 border-t border-amber-100">
                  Similar: {similars.map(s => (
                    <button key={s.id} className="underline mr-1 hover:text-amber-900" onClick={() => handleSelect(s)}>{s.name}</button>
                  ))}
                </div>
              )}
              <button onClick={handleCreate} disabled={creating}
                className="w-full text-left px-2 py-1 text-emerald-700 hover:bg-emerald-50 font-semibold border-t border-gray-100">
                {creating ? 'Creating…' : `+ Create "${trimmed}"`}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Generic searchable picker ───────────────────────────────────────────────
// A <select> with 139 options is not a picker, it is a scrolling exercise. Same
// component serves the Sage ledger list, which is longer still.
function SearchSelect({ options, value, onChange, placeholder = 'Search…', emptyLabel = '— none —', className }) {
  const [query, setQuery] = useState('');
  const [open, setOpen]   = useState(false);
  const wrapRef = useRef();

  useEffect(() => {
    const h = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const selected = options.find(o => o.value === value);
  const q = query.trim().toLowerCase();
  const filtered = q ? options.filter(o => o.label.toLowerCase().includes(q)) : options;

  return (
    <div ref={wrapRef} className="relative">
      <input
        className={className}
        value={open ? query : (selected?.label || '')}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => { setQuery(''); setOpen(true); }}
        placeholder={placeholder}
      />
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-0.5 bg-white border border-gray-200 rounded shadow-lg max-h-56 overflow-y-auto text-[9px]">
          <button className="w-full text-left px-2 py-1 text-gray-400 hover:bg-gray-50 border-b border-gray-100"
            onClick={() => { onChange(''); setOpen(false); }}>{emptyLabel}</button>
          {filtered.slice(0, 200).map(o => (
            <button key={o.value} className={`w-full text-left px-2 py-1 hover:bg-blue-50 ${o.value === value ? 'bg-blue-50 font-semibold text-navy' : 'text-gray-700'}`}
              onClick={() => { onChange(o.value); setOpen(false); }}>
              {o.label}
            </button>
          ))}
          {!filtered.length && <div className="px-2 py-1 text-gray-400 italic">No match</div>}
        </div>
      )}
    </div>
  );
}

// ── VAT read-out ────────────────────────────────────────────────────────────
// Mirrors deriveVatLines on the server so the person entering the invoice sees
// the same verdict the posting will reach, while they can still do something
// about it. Kept deliberately simple: the server is the authority, this is the
// early warning.
const VAT_TREATMENTS = [
  ['',               '— not established —'],
  ['standard',       'Standard rated (20%)'],
  ['reduced',        'Reduced rate (5%)'],
  ['zero',           'Zero rated (0%)'],
  ['exempt',         'Exempt from VAT'],
  ['reverse_charge', 'Reverse charge'],
  ['not_registered', 'Supplier not VAT registered'],
];

function vatVerdict({ total_excl_vat, vat_amount, total_incl_vat, supplier_vat_number, vat_treatment }, lines = []) {
  const net = parseFloat(total_excl_vat);
  const vat = parseFloat(vat_amount) || 0;
  const gross = parseFloat(total_incl_vat);
  if (!Number.isFinite(net) || net <= 0) return null;

  if (Number.isFinite(gross) && Math.abs(net + vat - gross) > 0.011) {
    return { tone: 'error', text: `Net + VAT = ${(net + vat).toFixed(2)}, but the total says ${gross.toFixed(2)}. One of the three is wrong.` };
  }

  // Real lines beat the arithmetic. Describing the header split while the
  // invoice carries its own lines would tell somebody the opposite of what is
  // about to happen.
  const usable = lines.filter(l => l.net_amount !== '' && l.net_amount != null);
  if (usable.length) {
    const sumNet = usable.reduce((t, l) => t + (parseFloat(l.net_amount) || 0), 0);
    const sumVat = usable.reduce((t, l) => t + (parseFloat(l.vat_amount) || 0), 0);
    if (Math.abs(sumNet - net) > 0.05 || Math.abs(sumVat - vat) > 0.05) {
      return { tone: 'error', text: `The lines below add up to £${sumNet.toFixed(2)} + £${sumVat.toFixed(2)} VAT, which does not match the invoice total. Fix them before posting.` };
    }
    const rates = [...new Set(usable.map(l => Number(l.vat_rate_percent ?? 0)))].sort((a, b) => b - a);
    if (vat > 0 && !supplier_vat_number) {
      return { tone: 'warn', text: `${usable.length} lines at ${rates.map(r => `${r}%`).join(' and ')} — but no supplier VAT number. A VAT invoice must show one for the reclaim to stand up.` };
    }
    return {
      tone: 'ok',
      text: `${usable.length} lines at ${rates.map(r => `${r}%`).join(' and ')}, £${vat.toFixed(2)} reclaimable. Each posts to Sage at its own rate.`,
    };
  }
  const pct = Math.round(vat / net * 1000) / 10;
  for (const r of [20, 5, 0]) {
    if (Math.abs(net * r / 100 - vat) <= 0.05) {
      if (r === 0 && !vat_treatment) {
        return { tone: 'warn', text: `No VAT on a £${net.toFixed(2)} invoice. Set the reason below, or about £${(net * 0.2).toFixed(2)} of reclaim may be going missing.` };
      }
      if (r > 0 && !supplier_vat_number) {
        return { tone: 'warn', text: `${r}% VAT — but no supplier VAT number. A VAT invoice must show one for the reclaim to stand up.` };
      }
      return { tone: 'ok', text: `${r}% VAT, £${vat.toFixed(2)} reclaimable.` };
    }
  }
  const standardNet = Math.round(vat / 0.20 * 100) / 100;
  if (vat > 0 && standardNet < net) {
    return { tone: 'warn', text: `Works out at ${pct}% — looks like a mixed invoice: £${standardNet.toFixed(2)} at 20% and £${(net - standardNet).toFixed(2)} at 0%. It will post as two lines.` };
  }
  return { tone: 'error', text: `The VAT is ${pct}% of the net, which is not a UK rate. Check the figures against the invoice.` };
}

// ── Invoice lines ───────────────────────────────────────────────────────────
//
// An invoice header carries one net, one VAT and one gross, so an invoice mixing
// VAT rates can only be approximated from it. Lines make the split a fact read
// off the document instead of an inference, and let one invoice hit several
// nominal codes — which is the bit AutoEntry does that a header alone cannot.
//
// Optional throughout: an invoice with no lines behaves exactly as before.
const LINE_RATES = [
  { v: 20, label: '20%' },
  { v: 5,  label: '5%' },
  { v: 0,  label: '0%' },
];

function LinesEditor({ lines, setLines, headerNet, headerVat, headerLedgerId, headerLedgerLabel, ledgerOptions, inp, lbl }) {
  const sumNet = lines.reduce((t, l) => t + (parseFloat(l.net_amount) || 0), 0);
  const sumVat = lines.reduce((t, l) => t + (parseFloat(l.vat_amount) || 0), 0);
  const net = parseFloat(headerNet) || 0;
  const vat = parseFloat(headerVat) || 0;

  // What a new line starts as: whatever was chosen for the invoice as a whole.
  //
  // A hard-coded 20% was wrong on every zero-rated and exempt invoice, and it
  // was wrong quietly — the line said 20%, the amount said nothing, and the
  // post was refused later. The rate the invoice's own totals work out at is a
  // far better guess, and it is only a starting value: the rate box is right
  // there.
  const headerRate = (() => {
    if (!(net > 0)) return 20;
    const pct = Math.round(vat / net * 1000) / 10;
    const known = [20, 5, 0].find(r => Math.abs(pct - r) < 0.6);
    return known != null ? known : 20;
  })();

  // Same idea for the nominal code. A line with no code of its own posts under
  // the invoice's, which was true before but invisible: the box said
  // "invoice default" and never said what that default WAS, so the only way to
  // check was to post it and look in Sage.
  const inheritLabel = headerLedgerLabel ? `— ${headerLedgerLabel} —` : '— invoice default —';
  const useHeaderCodeEverywhere = () =>
    setLines(lines.map(l => ({ ...l, sage_ledger_account_id: '' })));
  const overridden = headerLedgerId
    ? lines.filter(l => l.sage_ledger_account_id && l.sage_ledger_account_id !== headerLedgerId).length
    : 0;
  // Five pence, matching the server: long invoices round per line.
  const netOk = Math.abs(sumNet - net) <= 0.05;
  const vatOk = Math.abs(sumVat - vat) <= 0.05;

  const update = (i, patch) => setLines(lines.map((l, j) => j === i ? { ...l, ...patch } : l));

  // Two ways in, and both have to work.
  //
  // Pick a rate and the amount follows: the fast path, because the rate is
  // usually what somebody knows.
  const setRate = (i, pct) => {
    const n = parseFloat(lines[i].net_amount) || 0;
    update(i, { vat_rate_percent: pct, vat_amount: (Math.round(n * pct) / 100).toFixed(2) });
  };

  // Type the amount off the invoice and the RATE follows. Without this the
  // dropdown kept saying 20% while the amount said something else, and the post
  // was then refused with "20% of 150.00 is 30.00 but the line says 7.50" —
  // technically correct and useless, because the typed figure was the one read
  // off the document and the stale dropdown was the wrong half.
  const setVatAmount = (i, raw) => {
    const n = parseFloat(lines[i].net_amount);
    const v = parseFloat(raw);
    const patch = { vat_amount: raw };
    if (Number.isFinite(n) && n > 0 && Number.isFinite(v)) {
      patch.vat_rate_percent = Math.round(v / n * 1000) / 10;
    }
    update(i, patch);
  };

  // A rate that is not a UK one, said on the line rather than saved up for the
  // post. Usually a typo in either box, and it is a five-second fix here.
  const oddRate = (l) => {
    const p = Number(l.vat_rate_percent);
    if (!Number.isFinite(p)) return null;
    return [20, 5, 0].some(r => Math.abs(p - r) < 0.051) ? null : p;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-0.5">
        <label className={lbl}>Lines {lines.length ? `(${lines.length})` : ''}</label>
        <button type="button" onClick={() => setLines([...lines, { description: '', net_amount: '', vat_amount: '', vat_rate_percent: headerRate, sage_ledger_account_id: '' }])}
          className="text-[7px] px-1.5 py-0.5 border border-gray-200 rounded text-gray-600 hover:bg-gray-50">+ Line</button>
      </div>

      {!lines.length ? (
        <div className="text-[7px] text-gray-400 border border-dashed border-gray-200 rounded px-2 py-1.5">
          No lines. The invoice posts as a single entry, split by VAT rate if it needs to be.
          Add lines when the invoice mixes VAT rates or spans nominal codes.
        </div>
      ) : (
        <div className="space-y-1">
          {lines.map((l, i) => (
            <div key={i} className="border border-gray-200 rounded p-1.5 space-y-1 bg-gray-50/50">
              <div className="flex gap-1">
                <input className={`${inp} flex-1`} placeholder="Description"
                  value={l.description || ''} onChange={e => update(i, { description: e.target.value })} />
                <button type="button" onClick={() => setLines(lines.filter((_, j) => j !== i))}
                  className="text-[8px] text-gray-400 hover:text-red-500 px-1">✕</button>
              </div>
              <div className="grid grid-cols-4 gap-1">
                <input className={inp} type="number" step="0.01" placeholder="Net"
                  value={l.net_amount} onChange={e => update(i, { net_amount: e.target.value })}
                  onBlur={() => setRate(i, l.vat_rate_percent ?? 20)} />
                <select className={inp} value={l.vat_rate_percent ?? 20}
                  onChange={e => setRate(i, Number(e.target.value))}>
                  {LINE_RATES.map(r => <option key={r.v} value={r.v}>{r.label}</option>)}
                  {oddRate(l) !== null && <option value={l.vat_rate_percent}>{oddRate(l)}% ?</option>}
                </select>
                <input className={inp} type="number" step="0.01" placeholder="VAT"
                  value={l.vat_amount} onChange={e => setVatAmount(i, e.target.value)} />
                {ledgerOptions.length > 0 && (
                  // The placeholder is the invoice's own code, not the word
                  // "Code". An empty box that says nothing looks like a missing
                  // answer; an empty box showing the code it will actually post
                  // under says the default is already right, which on most
                  // invoices it is.
                  <SearchSelect options={ledgerOptions} value={l.sage_ledger_account_id || ''}
                    onChange={v => update(i, { sage_ledger_account_id: v })}
                    placeholder={headerLedgerLabel || 'Code'} emptyLabel={inheritLabel} className={inp} />
                )}
              </div>
              {oddRate(l) !== null && (
                <div className="text-[7px] text-amber-800">
                  ⚠ £{parseFloat(l.vat_amount) || 0} on £{parseFloat(l.net_amount) || 0} works out at {oddRate(l)}%,
                  which is not a UK rate. Check both boxes against the invoice — Sage has no rate to post this at.
                </div>
              )}
            </div>
          ))}

          {/* The header totals are what the supplier billed, so lines that do
              not add up to them are a reading error. Said here, while it is
              still cheap to fix, rather than at post time. */}
          <div className={`text-[7px] px-2 py-1 rounded border ${
            netOk && vatOk ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                           : 'bg-red-50 border-red-200 text-red-700'}`}>
            Lines total £{sumNet.toFixed(2)} net + £{sumVat.toFixed(2)} VAT.
            {netOk && vatOk
              ? ' Matches the invoice.'
              : ` The invoice says £${net.toFixed(2)} + £${vat.toFixed(2)}, and these must agree before it can post.`}
          </div>

          {/* Which lines are not on the invoice's code, and the way back. Said
              out loud because a per-line override is easy to set by accident
              and impossible to spot once the form is closed. */}
          {overridden > 0 && (
            <div className="text-[7px] text-gray-500 flex items-center gap-1 flex-wrap">
              <span>
                {overridden} of {lines.length} lines {overridden === 1 ? 'has' : 'have'} its own nominal code.
                The rest post to {headerLedgerLabel || 'the invoice default'}.
              </span>
              <button type="button" onClick={useHeaderCodeEverywhere}
                className="px-1.5 py-0.5 border border-gray-200 rounded bg-white text-gray-600 hover:bg-gray-50">
                Put them all on the invoice code
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Status config ───────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  unprocessed:      { label: 'Unprocessed',      dot: '#60A5FA', pill: 'bg-blue-50 text-blue-600' },
  not_applicable:   { label: 'Not Applicable',   dot: '#D1D5DB', pill: 'bg-slate-100 text-slate-500' },
  invoice_received: { label: 'Invoice Received', dot: '#FCD34D', pill: 'bg-amber-50 text-amber-700' },
  matched:          { label: 'Matched',          dot: '#34D399', pill: 'bg-emerald-50 text-emerald-700' },
};
const CURRENCIES = ['GBP', 'EUR', 'USD'];

// ── GOODS OR SERVICES, ON A PURCHASE FROM THE EU ─────────────────────────────
//
// Sage refuses a purchase invoice against an EU contact until every line says
// which it is, because an acquisition from the EU is reported differently for
// each. Nothing on the document distinguishes them, so it is asked here rather
// than guessed at post time.
//
// Mirrors euGoodsServicesRequired on the server, for the same reason vatVerdict
// mirrors deriveVatLines: the server is the authority, this is what decides
// whether the question is worth putting on screen. The list is the 27 member
// states; GB and XI are absent because a UK purchase needs no classification.
const EU_MEMBER_STATES = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU',
  'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
]);
const EU_GOODS_SERVICES = [
  ['',          '— not chosen —'],
  ['goods',     'Goods'],
  ['services',  'Services'],
];
// A VAT number carries its country in the first two letters, and Greece prints
// EL where its country code is GR.
const euCountryFromVat = (vatNumber) => {
  const prefix = String(vatNumber || '').replace(/[^A-Za-z]/g, '').slice(0, 2).toUpperCase();
  const code = prefix === 'EL' ? 'GR' : prefix;
  return EU_MEMBER_STATES.has(code) ? code : null;
};

// WHAT KIND OF FILE IS THIS? THE NAME, WHEN THE TYPE SAYS NOTHING.
//
// Mail clients routinely declare every attachment as "application/octet-stream".
// Testing content_type for 'pdf' therefore missed real PDFs: the Extract Invoice
// button did not appear, and the viewer could not tell it had something it could
// show. The server now stores the resolved type, but these checks stay
// filename-aware for the attachments synced before that and for anything else
// arriving mislabelled.
const attachmentIsPdf = (att) => {
  const ct = (att?.content_type || '').toLowerCase();
  const fn = (att?.filename || '').toLowerCase();
  return ct.includes('pdf') || fn.endsWith('.pdf');
};

// Only PDFs and document types are actionable — skip inline images (logos, signatures, etc.)
const isDocumentAttachment = (att) => {
  const ct = (att.content_type || '').toLowerCase();
  const fn = (att.filename || '').toLowerCase();
  // An image declared as octet-stream would otherwise be treated as a document and
  // offered for extraction.
  if (/\.(png|jpe?g|gif|webp|bmp|tiff?)$/.test(fn)) return false;
  return !ct.startsWith('image/');
};

function StatusPill({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, pill: 'bg-gray-100 text-gray-500' };
  return <span className={`text-[7px] font-bold px-1.5 py-0.5 rounded-full ${cfg.pill}`}>{cfg.label}</span>;
}

function timeAgo(ts) {
  if (!ts) return 'never';
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ── Inline invoice form (no modal) ──────────────────────────────────────────
// ── A HALF-FINISHED INVOICE SURVIVES A RELOAD ────────────────────────────────
//
// "It just refreshed the page, lost everything I was doing." The refreshing is
// fixed in main.jsx — nothing reloads on its own any more — but a reload can still
// happen: somebody presses the update notice, hits F5, the browser crashes, or the
// error boundary recovers from a genuinely broken build. Reading an invoice off a
// PDF is five minutes of careful work, and none of it should depend on the tab
// staying alive.
//
// So the form is kept in sessionStorage as it is edited, and restored on the way
// back. Session-scoped rather than local: a draft is only interesting for as long
// as the tab is, and it must not outlive the invoice being logged by somebody else.
const draftKeyFor = (emailId, attachmentId, editId) =>
  `av-invoice-draft:${emailId || 'none'}:${attachmentId || 'none'}:${editId || 'new'}`;

const readDraft = (key) => {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const d = JSON.parse(raw);
    return d && typeof d === 'object' ? d : null;
  } catch { return null; }
};
const writeDraft = (key, value) => {
  try { sessionStorage.setItem(key, JSON.stringify(value)); } catch { /* full or blocked */ }
};
const clearDraft = (key) => {
  try { sessionStorage.removeItem(key); } catch { /* blocked */ }
};

function InvoiceForm({ emailId, attachmentId, initial, suppliers, sows, pos, overheadCategories, sageRef, autoentryEnabled, sagePostingEnabled, resourceWorkers = [], contractorMatch = null, senderEmail = null, onSetSupplierDefault, onSetSupplierEuDefault, onSageRefresh, onSave, onCancel, onSupplierCreated, isAiProposed, hasAttachment, supplierSuggestion }) {
  const draftKey = draftKeyFor(emailId, attachmentId, initial?.id);
  // Read once, synchronously, so a restored form never flashes its blank version.
  const [draft] = useState(() => readDraft(draftKey));
  const [restored, setRestored] = useState(!!draft);
  const [form, setForm] = useState({
    invoice_number:       initial?.invoice_number       || '',
    invoice_date:         initial?.invoice_date         || '',
    // Invoice or credit note. Set by the extractor when the document says so,
    // and switchable on the form because the AI can miss one and older records
    // were all logged as invoices.
    document_type:        initial?.document_type        || 'invoice',
    supplier_name:        initial?.supplier_name        || '',
    supplier_id:          initial?.supplier_id          || '',
    supplier_vat_number:  initial?.supplier_vat_number  || '',
    vat_treatment:        initial?.vat_treatment        || '',
    eu_goods_services:    initial?.eu_goods_services    || '',
    total_excl_vat:       initial?.total_excl_vat       ?? '',
    vat_amount:           initial?.vat_amount           ?? '',
    total_incl_vat:       initial?.total_incl_vat       ?? '',
    currency:             initial?.currency             || 'GBP',
    po_id:                initial?.po_id                || '',
    sow_id:               initial?.sow_id               || '',
    overhead_category_id: initial?.overhead_category_id || '',
    sage_ledger_account_id: initial?.sage_ledger_account_id || '',
    payment_due_date:     initial?.payment_due_date     || '',
    payment_terms_days:   initial?.payment_terms_days   ?? '',
    payment_terms_text:   initial?.payment_terms_text   || '',
    payment_due_source:   initial?.payment_due_source   || '',
    notes:                initial?.notes                || '',
    status:               initial?.status               || 'pending',
    // Contractor invoices only. Held on the same form object because they are
    // the same act of logging an invoice, just against a person rather than a
    // company — see the type switch below.
    resource_worker_id:   initial?.resource_worker_id   || contractorMatch?.id || '',
    contract_id:          initial?.contract_id          || '',
    labour_days:          initial?.labour_days          ?? '',
    period_from:          initial?.period_from          || '',
    period_to:            initial?.period_to            || '',
    // A saved draft is the most recent version of this form, so it wins over the
    // values read off the document.
    ...(draft?.form || {}),
  });

  // WHAT KIND OF INVOICE THIS IS.
  //
  // It used to be asked before anything else, as a separate button on the email
  // that opened a different modal with its own extract button, its own fields
  // and its own idea of what an invoice looks like. So the first thing anybody
  // had to do was classify a document they had not read yet, and getting it
  // wrong meant starting again in the other screen.
  //
  // It is a property of the invoice, not a route to a different screen. One
  // form, one extraction, and this switch: the document is read first and
  // classified after, which is the order a person actually does it in.
  //
  // And where the app can tell, it answers the question itself: an email from
  // an address that belongs to a registered contractor is a contractor invoice
  // far more often than not. It is a default, not a decision — the switch is
  // right there and costs one click to correct.
  const [invoiceType, setInvoiceType] = useState(
    draft?.invoiceType
      || ((initial?.resource_worker_id || (!initial?.id && contractorMatch)) ? 'contractor' : 'supplier'));
  const isContractor = invoiceType === 'contractor';
  // Lines are edited alongside the header but saved through their own endpoint,
  // because they are replaced as a set rather than patched field by field.
  const [lines, setLines] = useState(draft?.lines || initial?.lines || []);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const [saving, setSaving] = useState(false); // false | 'log' | 'autoentry'

  // Kept current as it is edited. Written on every change rather than debounced:
  // the object is small and sessionStorage is synchronous, and a debounce is one
  // more way to lose the last few keystrokes — which is the thing this exists to
  // prevent.
  useEffect(() => {
    writeDraft(draftKey, { form, lines, invoiceType, at: new Date().toISOString() });
  }, [draftKey, form, lines, invoiceType]);
  const [creatingSupplier, setCreatingSupplier] = useState(false);

  // The same "which supplier record is this" answer for an invoice typed by
  // hand, asked when the printed name is finished with and no record is chosen.
  // Nothing is overwritten: if somebody has already picked a record, their
  // choice stands.
  const [lookedUpSupplier, setLookedUpSupplier] = useState(null);
  const suggestion = supplierSuggestion || lookedUpSupplier;
  const lookUpSupplier = async () => {
    if (form.supplier_id || !form.supplier_name.trim()) return;
    const r = await api.get('procurement/suggest-supplier',
      { name: form.supplier_name, sender: senderEmail || '' }).catch(() => null);
    if (!r?.suggestion) return;
    setLookedUpSupplier(r.suggestion);
    if (r.suggestion.supplier_id) set('supplier_id', r.suggestion.supplier_id);
  };

  // Leaving the net and letting the gross follow. It does NOT suggest a VAT
  // amount: an empty VAT box is the single biggest cause of unclaimed input VAT,
  // and the suggestion for that is offered explicitly below rather than applied
  // behind somebody's back.
  const autoCalc = () => {
    const excl = parseFloat(form.total_excl_vat) || 0;
    const vat  = parseFloat(form.vat_amount)     || 0;
    if (excl > 0) set('total_incl_vat', (excl + vat).toFixed(2));
  };

  // The suggestion, offered only when the VAT box has never been touched.
  //
  // '' and '0' are deliberately different. Empty means nobody has said, and that
  // is what quietly loses a reclaim. A typed zero is a decision — the invoice
  // really is zero-rated — and overwriting it with 20% would declare VAT the
  // supplier never charged. So this only ever fires on the empty case, and only
  // when somebody presses it.
  const suggestedVat = (() => {
    const net = parseFloat(form.total_excl_vat);
    if (!Number.isFinite(net) || net <= 0) return null;
    if (String(form.vat_amount).trim() !== '') return null;
    return Math.round(net * 20) / 100;
  })();

  const applySuggestedVat = () => {
    const net = parseFloat(form.total_excl_vat) || 0;
    setForm(p => ({
      ...p,
      vat_amount: suggestedVat.toFixed(2),
      total_incl_vat: (net + suggestedVat).toFixed(2),
      vat_treatment: p.vat_treatment || 'standard',
    }));
  };

  // One save, three destinations. 'log' records it and stops there; 'autoentry'
  // also forwards it; 'sage' saves it and asks for the Sage checks straight
  // away, so the figures on screen go from read to checked without anybody
  // having to find the invoice again afterwards. None of the three posts
  // anything: 'sage' produces a preview and a Post button, nothing more.
  const handleSave = async (mode = 'log') => {
    setSaving(mode);
    try {
      await onSave({
        ...form,
        // Which kind of record this becomes. The two go to different tables,
        // which is precisely why the choice used to be made before the form
        // rather than on it — but that is a fact about the plumbing, not
        // something the person logging an invoice should have to know first.
        invoice_type: invoiceType,
        payment_terms_days: form.payment_terms_days === '' ? null : Number(form.payment_terms_days),
        lines: lines.filter(l => l.net_amount !== '' && l.net_amount != null).map(l => ({
          ...l,
          net_amount: parseFloat(l.net_amount),
          vat_amount: parseFloat(l.vat_amount) || 0,
        })),
        email_id: emailId, attachment_id: attachmentId,
        send_autoentry: mode === 'autoentry',
        prepare_sage: mode === 'sage',
      });
      // Saved, so the draft has done its job. Cleared only on SUCCESS — a save
      // that failed is exactly when the typed version still matters.
      clearDraft(draftKey);
    } finally {
      setSaving(false);
    }
  };

  // Cancelling is a decision to abandon it, so the draft goes too. Confirmed
  // first when there is a restored or edited draft behind it, because losing work
  // to a misclick is the same loss by another route.
  const cancelForm = () => {
    clearDraft(draftKey);
    onCancel?.();
  };

  const inp = 'w-full border border-gray-200 rounded px-2 py-1 text-[9px] font-[Montserrat] bg-white focus:outline-none focus:border-navy';
  const lbl = 'block text-[7px] font-bold text-gray-400 uppercase mb-0.5';
  const isNew = !initial?.id;
  // The combined action is only offered where posting could actually follow it:
  // Sage connected and switched on, and this invoice neither already in Sage nor
  // already gone to AutoEntry. Anywhere else it would produce a checked invoice
  // and a Post button that refuses.
  const canPrepareSage = !!sagePostingEnabled && !isContractor
    && !initial?.sage_invoice_id && !initial?.autoentry_sent_at;

  const verdict = vatVerdict(form, lines);
  const ledgerOptions = (sageRef?.ledger_accounts || []).map(l => ({
    value: l.id,
    label: `${l.nominal_code ? `${l.nominal_code} — ` : ''}${l.display_name || l.name || ''}`,
  }));

  // Where the nominal code would come from if none is chosen here. Shown so the
  // default is visible rather than something that happens silently at push time.
  //
  // The supplier list from /sage/reference carries the defaults; the plain
  // suppliers list does not, so it is preferred when available.
  const chosenSupplier = (sageRef?.suppliers || []).find(s => s.id === form.supplier_id)
    || suppliers.find(s => s.id === form.supplier_id);
  const sageContactName = (sageRef?.contacts || []).find(c => c.id === chosenSupplier?.sage_contact_id)?.name;
  const supplierDefault = chosenSupplier?.default_sage_ledger_account_id;
  const categoryDefault = overheadCategories.find(c => c.id === form.overhead_category_id)?.sage_ledger_account_id;
  const inheritedLedger = !form.sage_ledger_account_id && (supplierDefault || categoryDefault);
  const inheritedLabel = inheritedLedger
    ? ledgerOptions.find(o => o.value === inheritedLedger)?.label
    : null;
  const supplierDefaultLabel = supplierDefault
    ? ledgerOptions.find(o => o.value === supplierDefault)?.label
    : null;

  // ── IS THIS AN EU PURCHASE? ────────────────────────────────────────────────
  //
  // Three signals, and any one of them puts the question on screen. The Sage
  // contact's country is how Sage itself decides. The VAT number on the invoice
  // covers a contact whose address in Sage was never filled in. And a supplier
  // Sage has already refused a posting for is settled, whatever the other two
  // say.
  //
  // The question is also shown whenever an answer has already been given, so a
  // value that is being sent to Sage is never invisible on the form that set it.
  const euCountry = chosenSupplier?.sage_contact_country
    && EU_MEMBER_STATES.has(String(chosenSupplier.sage_contact_country).toUpperCase().slice(0, 2))
    ? String(chosenSupplier.sage_contact_country).toUpperCase().slice(0, 2)
    : euCountryFromVat(form.supplier_vat_number);
  const euSupplierDefault = chosenSupplier?.eu_goods_services || '';
  const askEuGoodsServices = !!euCountry
    || !!chosenSupplier?.eu_goods_services_required
    || !!form.eu_goods_services
    || !!euSupplierDefault;
  const euTypesSynced = (sageRef?.eu_goods_services_types || []).length > 0;

  // THE SUPPLIER'S USUAL NOMINAL CODE, FILLED IN RATHER THAN IMPLIED.
  //
  // The default was only ever applied at post time, with a line of small print
  // under an empty picker saying which code would be used. That is not what "we
  // should default a ledger per supplier" means in practice: the field a person
  // is looking at was still empty, the checks still said no code had been
  // chosen, and the way to satisfy them was to pick the same code by hand every
  // time.
  //
  // So it is put IN the field, where it can be seen, changed, or left alone. The
  // resolution order is unchanged (supplier, then the category's suggestion) and
  // the post-time fallback still stands for invoices logged before this.
  //
  // Keyed on supplier + category so choosing a different supplier brings its own
  // code, while an override typed afterwards is never overwritten — the fill only
  // ever happens into an empty field.
  const autoFilledLedgerFor = useRef(null);
  useEffect(() => {
    if (isContractor) return;
    const key = `${form.supplier_id || ''}|${form.overhead_category_id || ''}`;
    if (autoFilledLedgerFor.current === key) return;
    const dflt = supplierDefault || categoryDefault;
    autoFilledLedgerFor.current = key;
    if (dflt && !form.sage_ledger_account_id) set('sage_ledger_account_id', dflt);
  }, [form.supplier_id, form.overhead_category_id, form.sage_ledger_account_id,
      supplierDefault, categoryDefault, isContractor]);

  // ── What still stands between this invoice and Sage ─────────────────────────
  //
  // Every one of these used to be found out AFTER logging, on the invoice's Sage
  // panel, which is the wrong end: by then the PDF has been closed, the person
  // has moved to the next email, and the invoice sits in ops linked to nothing.
  // Sage will not take a purchase invoice without a contact, so an invoice whose
  // supplier is not in Sage is not a warning, it is a dead record.
  //
  // Asked here instead, while the document is still open and the supplier is
  // still the thing being thought about.
  const sageBlockers = [];
  if (!isContractor && sagePostingEnabled) {
    if (!form.supplier_id) {
      sageBlockers.push({
        key: 'supplier',
        text: 'No supplier record chosen, so there is nothing to link to Sage.',
        fix: null,
      });
    } else if (sageRef && !chosenSupplier?.sage_contact_id) {
      sageBlockers.push({
        key: 'contact',
        text: `${chosenSupplier?.name || 'This supplier'} is not in Sage yet. Sage will not accept an invoice without a contact.`,
        fix: 'create-supplier',
      });
    }
  }
  // The nominal code. Sage refuses a purchase invoice without one, so an
  // invoice logged without a code anywhere is the same dead record as one with
  // no contact — it was a warning here and a blocker at post time, which meant
  // finding out afterwards.
  //
  // "Anywhere" is the important word: the code can come from this invoice, the
  // supplier's default, the category's default, or the fallback in Sage
  // settings. Only when all four are empty is there really nothing, and the
  // fallback is read from /sage/reference precisely so this can tell the
  // difference rather than blocking invoices that would have posted.
  const effectiveLedger = form.sage_ledger_account_id || inheritedLedger || sageRef?.default_ledger_account_id || null;
  if (!isContractor && sagePostingEnabled && sageRef && !effectiveLedger) {
    sageBlockers.push({
      key: 'ledger',
      text: 'No nominal code. Sage will not take the invoice without one, and none is set on the supplier, the category or in Sage settings.',
      fix: null,
    });
  }
  const sageWarnings = [];
  const sageReady = sageBlockers.length === 0;

  return (
    <div className="flex flex-col h-full">
      {/* Picked up where it was left off. Said out loud, with a way to throw it
          away: silently restoring old values would be its own kind of surprise. */}
      {restored && (
        <div className="bg-emerald-50 border border-emerald-200 rounded px-2.5 py-1.5 mb-2 flex items-center gap-2 flex-shrink-0">
          <span className="text-emerald-600 text-[9px]">↩</span>
          <span className="text-[8px] text-emerald-800 font-semibold flex-1">
            Restored what you had typed{draft?.at ? ` (${new Date(draft.at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })})` : ''}.
          </span>
          <button type="button"
            onClick={() => {
              clearDraft(draftKey);
              setRestored(false);
              // Back to the document's own values, which is what "start again"
              // means here.
              setForm(f => ({ ...f, ...(initial || {}) }));
              setLines(initial?.lines || []);
            }}
            className="text-[8px] font-semibold text-emerald-800 underline hover:text-emerald-900">
            Start again from the document
          </button>
        </div>
      )}
      {isAiProposed && (
        <div className="bg-blue-50 border border-blue-200 rounded px-2.5 py-1.5 mb-2 flex items-center gap-1.5 flex-shrink-0">
          <span className="text-blue-500 text-[9px]">✦</span>
          <span className="text-[8px] text-blue-700 font-semibold">AI-proposed values — please review before saving</span>
        </div>
      )}
      <div className="flex-1 overflow-y-auto space-y-2 pr-0.5">
        {/* One question, asked once, on the same form. It used to be a separate
            button on the email leading to a separate modal. */}
        {/* Locked once the invoice exists. The two are separate records in
            separate tables, so switching an invoice that has already been saved
            would not move it — it would write a second one and leave the first
            where it was. Delete and re-log it if the type was wrong. */}
        <div className="flex items-center gap-1 p-0.5 bg-gray-100 rounded">
          {[['supplier', '🏢 Supplier invoice'], ['contractor', '👷 Contractor invoice']].map(([v, label]) => (
            <button key={v} type="button" onClick={() => setInvoiceType(v)} disabled={!isNew}
              title={isNew ? '' : 'Set when the invoice was logged. Delete and log it again to change it.'}
              className={`flex-1 px-2 py-1 text-[8px] font-bold rounded transition-colors ${invoiceType === v
                ? 'bg-white text-navy shadow-sm'
                : 'text-gray-500 hover:text-gray-700'} ${!isNew && invoiceType !== v ? 'opacity-30 cursor-not-allowed' : ''}`}>
              {label}
            </button>
          ))}
        </div>
        {isContractor && (
          <div className="text-[7px] text-gray-500">
            Recorded against the contractor for allocation and approval, not posted to Sage from here.
          </div>
        )}

        {/* Invoice or credit note. Unlike the switch above this is one column on
            one record, so it stays editable after saving — that is how a credit
            note logged as an invoice gets corrected. It matters because the two
            post to different Sage endpoints: a credit note SUBTRACTS the VAT,
            and an invoice with a minus sign would get the ledger right by
            accident and the VAT return wrong on purpose. */}
        {!isContractor && (
          <>
            <div className="flex items-center gap-1 p-0.5 bg-gray-100 rounded">
              {[['invoice', '📄 Invoice'], ['credit_note', '↩ Credit note']].map(([v, label]) => (
                <button key={v} type="button" onClick={() => set('document_type', v)}
                  className={`flex-1 px-2 py-1 text-[8px] font-bold rounded transition-colors ${form.document_type === v
                    ? 'bg-white text-navy shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'}`}>
                  {label}
                </button>
              ))}
            </div>
            {form.document_type === 'credit_note' && (
              <div className="text-[7px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
                {isAiProposed ? '✦ Read off the document as a credit note — switch it above if that is wrong. ' : ''}
                Money back from the supplier. Enter the amounts as positive numbers: it posts to Sage as a
                purchase credit note, which reverses the VAT as well as the cost.
              </div>
            )}
          </>
        )}

        <div className="grid grid-cols-2 gap-2">
          <div><label className={lbl}>Invoice No.</label>
            <input className={inp} value={form.invoice_number} onChange={e => set('invoice_number', e.target.value)} placeholder="INV-001" /></div>
          <div><label className={lbl}>Invoice Date</label>
            <input className={inp} type="date" value={form.invoice_date} onChange={e => set('invoice_date', e.target.value)} /></div>
        </div>

        {isContractor ? (
          <>
            <div><label className={lbl}>Contractor *</label>
              <select className={inp} value={form.resource_worker_id}
                onChange={e => { set('resource_worker_id', e.target.value); set('contract_id', ''); }}>
                <option value="">— select contractor —</option>
                {resourceWorkers.map(w => {
                  const bracket = w.supplier_name || w.company_name;
                  return (
                    <option key={w.id} value={w.id}>
                      {w.first_name} {w.last_name}{bracket ? ` (${bracket})` : ''}
                    </option>
                  );
                })}
              </select>
              {!form.resource_worker_id ? (
                <div className="text-[7px] text-amber-700 mt-0.5">⚠ A contractor invoice has to be against a contractor.</div>
              ) : contractorMatch && form.resource_worker_id === contractorMatch.id ? (
                <div className="text-[7px] text-gray-500 mt-0.5">
                  ✦ Matched on the sender's email address. Change it above if that is wrong.
                </div>
              ) : null}
            </div>
            {/* The contract the days count against. Without one the invoice is
                paid but the contract's days used never move. */}
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2"><label className={lbl}>Contract</label>
                <ContractSelect workerId={form.resource_worker_id} value={form.contract_id}
                  onChange={id => set('contract_id', id)} className={inp} /></div>
              <div><label className={lbl}>Days billed</label>
                <input className={inp} type="number" min="0" step="0.5" value={form.labour_days}
                  onChange={e => set('labour_days', e.target.value)} placeholder="e.g. 5" />
                {isAiProposed && form.labour_days !== '' && (
                  <div className="text-[7px] text-gray-500 mt-0.5">✦ Read off the invoice.</div>
                )}</div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className={lbl}>Period From</label>
                <input className={inp} type="date" value={form.period_from} onChange={e => set('period_from', e.target.value)} /></div>
              <div><label className={lbl}>Period To</label>
                <input className={inp} type="date" value={form.period_to} onChange={e => set('period_to', e.target.value)} /></div>
            </div>
          </>
        ) : (
        <div className="grid grid-cols-2 gap-2">
          <div><label className={lbl}>Supplier (on invoice)</label>
            <input className={inp} value={form.supplier_name} onChange={e => set('supplier_name', e.target.value)}
              onBlur={lookUpSupplier} placeholder="name as on invoice" /></div>
          <div><label className={lbl}>Supplier record {sageRef ? <span className="text-gray-300 font-normal normal-case">(and its Sage link)</span> : null}</label>
            <SupplierCombobox
              suppliers={suppliers}
              value={form.supplier_id}
              onChange={id => set('supplier_id', id)}
              onCreated={onSupplierCreated}
              className={inp}
            />
            {/* The question this answers: "is that now Sage?" No — it picks the
                ops supplier record, and that record is what carries the Sage
                contact link. Two steps that looked like one, so the second one
                is now shown here rather than left to be discovered at post time. */}
            {sageRef && form.supplier_id && chosenSupplier?.sage_contact_id && (
              <div className="text-[7px] text-emerald-700 mt-0.5">
                ✓ Linked to Sage: {sageContactName || 'contact set'}
              </div>
            )}
            {/* Why this record was filled in, when it was filled in by the app
                rather than by the person. Choosing the same supplier for the
                same company on every invoice is a decision that only needs
                making once, so it is remembered — but a remembered answer that
                does not say it is remembered is indistinguishable from one
                somebody typed, and that is how a wrong default goes unnoticed. */}
            {suggestion?.reason && (
              suggestion.supplier_id && form.supplier_id === suggestion.supplier_id ? (
                <div className="text-[7px] text-gray-500 mt-0.5">
                  {suggestion.confidence === 'remembered' ? '↻ ' : '✦ '}
                  {suggestion.reason} Change it above if this is a different company.
                </div>
              ) : !form.supplier_id ? (
                <div className="text-[7px] text-amber-700 mt-0.5">⚠ {suggestion.reason}</div>
              ) : null
            )}</div>
        </div>
        )}

        {/* ── The Sage link, before the invoice is logged rather than after ──
            Sage will not take a purchase invoice without a contact, so an
            invoice logged against a supplier that is not in Sage is a record
            that can never be posted. It used to be discovered on the invoice's
            Sage panel afterwards, with the document closed and the person
            already on the next email. */}
        {sageBlockers.map(b => (
          <div key={b.key} className="px-2 py-1.5 rounded border border-red-200 bg-red-50">
            <div className="text-[8px] text-red-800 font-semibold">✕ {b.text}</div>
            {b.fix === 'create-supplier' && (
              <button type="button" onClick={() => setCreatingSupplier(true)}
                className="mt-1 px-2 py-0.5 text-[7px] font-bold border border-red-300 rounded bg-white text-red-800 hover:bg-red-100">
                Create it in Sage now
              </button>
            )}
            {b.key === 'supplier' && (
              <div className="text-[7px] text-red-700 mt-0.5">Pick one in "Supplier record" above, or create it there.</div>
            )}
            {b.key === 'ledger' && (
              <div className="text-[7px] text-red-700 mt-0.5">Choose one in "Sage Ledger Account" below.</div>
            )}
          </div>
        ))}
        {sageWarnings.map((w, i) => (
          <div key={`sw${i}`} className="px-2 py-1 rounded border border-amber-200 bg-amber-50 text-[7px] text-amber-800">⚠ {w}</div>
        ))}
        {!isContractor && sagePostingEnabled && sageReady && form.supplier_id && (
          <div className="px-2 py-1 rounded border border-emerald-200 bg-emerald-50 text-[7px] text-emerald-800">
            ✓ Ready for Sage: supplier linked{sageContactName ? ` to ${sageContactName}` : ''}, nominal code set.
          </div>
        )}

        <div className="grid grid-cols-3 gap-2">
          <div><label className={lbl}>Excl. VAT</label>
            <input className={inp} type="number" step="0.01" value={form.total_excl_vat}
              onChange={e => set('total_excl_vat', e.target.value)} onBlur={autoCalc} /></div>
          <div><label className={lbl}>VAT</label>
            <input className={inp} type="number" step="0.01" value={form.vat_amount}
              onChange={e => set('vat_amount', e.target.value)} onBlur={autoCalc} /></div>
          <div><label className={lbl}>Total incl. VAT</label>
            <input className={inp} type="number" step="0.01" value={form.total_incl_vat}
              onChange={e => set('total_incl_vat', e.target.value)} /></div>
        </div>

        {/* Most invoices are standard rated, so an empty VAT box gets an offer
            rather than a shrug. Offered, not applied: assuming 20% would claim
            VAT the supplier may never have charged, and assuming 0% loses the
            reclaim. Either way the app would have decided something only the
            document can settle. */}
        {suggestedVat !== null && (
          <div className="px-2 py-1.5 rounded border border-blue-200 bg-blue-50 flex items-center gap-2 flex-wrap">
            <span className="text-[8px] text-blue-900">
              No VAT entered. At the standard rate it would be <strong>£{suggestedVat.toFixed(2)}</strong>.
            </span>
            <button type="button" onClick={applySuggestedVat}
              className="px-1.5 py-0.5 text-[7px] font-semibold border border-blue-400 text-blue-900 rounded bg-white hover:bg-blue-100">
              Use 20%
            </button>
            <span className="text-[7px] text-blue-700">or type what the invoice says, including 0 if there is none.</span>
          </div>
        )}

        {/* The VAT verdict, next to the numbers it is about. Unclaimed input VAT
            is the most expensive thing this form can get wrong, and the only
            moment it is cheap to fix is while the invoice is on screen. */}
        {verdict && (
          <div className={`px-2 py-1.5 rounded border text-[8px] leading-snug ${
            verdict.tone === 'ok'   ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
            : verdict.tone === 'warn' ? 'bg-amber-50 border-amber-200 text-amber-800'
            : 'bg-red-50 border-red-200 text-red-700'}`}>
            {verdict.tone === 'ok' ? '✓ ' : '⚠ '}{verdict.text}
          </div>
        )}

        {/* VAT evidence, nominal codes and PO/SOW links are all about getting a
            purchase invoice into Sage. A contractor invoice is not going to
            Sage from here, so asking for them would be asking for nothing. */}
        {!isContractor && (<>
        <div className="grid grid-cols-2 gap-2">
          <div><label className={lbl}>Supplier VAT No.</label>
            <input className={inp} value={form.supplier_vat_number}
              onChange={e => set('supplier_vat_number', e.target.value)} placeholder="GB123456789" /></div>
          <div><label className={lbl}>VAT Treatment</label>
            <select className={inp} value={form.vat_treatment} onChange={e => set('vat_treatment', e.target.value)}>
              {VAT_TREATMENTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></div>
        </div>

        {/* ── Goods or services, on a purchase from the EU ──
            Only asked where Sage will ask, because on a UK invoice it is a
            question with no answer and one more field to skip past. An invoice
            from an EU supplier cannot post without it: Sage refuses the whole
            thing with "Please select the EU Goods/Services on each item line",
            and no amount of correcting the money makes that go away. */}
        {askEuGoodsServices && (
          <div>
            <label className={lbl}>EU goods or services</label>
            <select className={inp} value={form.eu_goods_services}
              onChange={e => set('eu_goods_services', e.target.value)}>
              {EU_GOODS_SERVICES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <div className="text-[7px] text-gray-500 mt-0.5 leading-relaxed">
              {euCountry
                ? `${chosenSupplier?.name || 'This supplier'} is in ${euCountry}, so Sage will not accept this invoice until every line says which it is.`
                : 'Sage has refused a posting for this supplier without it, so it is asked here.'}
              {' '}An acquisition from the EU is reported differently for goods and for services, and nothing on the
              invoice tells them apart.
            </div>
            {/* An empty picker with a plausible label is how this would fail
                silently: the ids come from Sage, and without them the choice
                cannot be turned into anything to send. */}
            {!euTypesSynced && (
              <div className="text-[7px] text-amber-800 mt-0.5">
                Sage has not sent its EU Goods/Services options yet. Press "Refresh from Sage" on the Sage screen,
                otherwise this answer cannot be turned into what Sage expects.
              </div>
            )}
            {euSupplierDefault && (
              <div className="text-[7px] text-gray-500 mt-0.5 flex items-center gap-1 flex-wrap">
                <span>Usual for {chosenSupplier?.name || 'this supplier'}:</span>
                <span className="text-gray-700 font-semibold">
                  {EU_GOODS_SERVICES.find(([v]) => v === euSupplierDefault)?.[1] || euSupplierDefault}
                </span>
                {form.eu_goods_services && form.eu_goods_services !== euSupplierDefault && (
                  <>
                    <span className="text-amber-700">· overridden for this invoice</span>
                    <button type="button" onClick={() => set('eu_goods_services', '')}
                      className="underline text-gray-500 hover:text-navy">use the usual one</button>
                  </>
                )}
              </div>
            )}
            {/* Remembered against the supplier, the same way the nominal code is:
                it is the same answer nearly every time, and asking per invoice is
                asking somebody to repeat themselves until they stop reading. */}
            {form.supplier_id && form.eu_goods_services && form.eu_goods_services !== euSupplierDefault && (
              <button type="button" onClick={() => onSetSupplierEuDefault?.(form.supplier_id, form.eu_goods_services)}
                className="text-[7px] mt-0.5 px-1.5 py-0.5 border border-gray-200 rounded text-gray-600 hover:bg-gray-50">
                {euSupplierDefault
                  ? `Make this the usual answer for ${chosenSupplier?.name || 'this supplier'} instead`
                  : `Remember this for ${chosenSupplier?.name || 'this supplier'}`}
              </button>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <div><label className={lbl}>Currency</label>
            <select className={inp} value={form.currency} onChange={e => set('currency', e.target.value)}>
              {CURRENCIES.map(c => <option key={c}>{c}</option>)}
            </select></div>
          {/* This field only ever did one thing: "rejected" excludes an invoice
              from every report, queue and VAT figure. Nothing anywhere treats
              "pending" and "matched" differently, so the old labels implied a
              workflow that did not exist — and now there IS a real lifecycle
              (logged, coded, checked, posted, verified) tracked elsewhere, they
              were actively misleading. The values are unchanged for
              compatibility; the labels now say what they do. */}
          <div><label className={lbl}>Include in reports</label>
            <select className={inp} value={form.status} onChange={e => set('status', e.target.value)}>
              <option value="pending">Yes — normal</option>
              <option value="matched">Yes — checked off</option>
              <option value="rejected">No — exclude this invoice entirely</option>
            </select>
            <div className="text-[7px] text-gray-400 mt-0.5">
              {form.status === 'rejected'
                ? 'Excluded from spend, VAT reclaim, the Sage queue and duplicate checks.'
                : 'Counts towards spend, VAT reclaim and the Sage queue. "Checked off" is just a marker for your own use.'}
            </div>
          </div>
        </div>

        {/* Where this invoice actually is, as opposed to the field above. Only
            shown when editing, because a new invoice has no lifecycle yet. */}
        {initial?.id && (
          <div className={`px-2 py-1 rounded border text-[8px] ${
            initial.sage_verified_at && !initial.sage_verify_issues ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
            : initial.sage_invoice_id ? 'bg-blue-50 border-blue-200 text-blue-800'
            : initial.autoentry_sent_at ? 'bg-indigo-50 border-indigo-200 text-indigo-800'
            : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
            {initial.sage_verified_at && !initial.sage_verify_issues
              ? `Confirmed in Sage${initial.sage_invoice_number ? ` as ${initial.sage_invoice_number}` : ''}.`
              : initial.sage_invoice_id
                ? `In Sage${initial.sage_invoice_number ? ` as ${initial.sage_invoice_number}` : ''}, not verified since.`
                : initial.autoentry_sent_at
                  ? 'Sent to AutoEntry. Ops will not post it as well.'
                  : 'Logged in ops. Not sent anywhere yet.'}
          </div>
        )}
        <div><label className={lbl}>Link to PO</label>
          <PoSelect pos={pos} value={form.po_id} onChange={id => set('po_id', id)} placeholder="— not matched —" inputClassName={inp} /></div>
        <div><label className={lbl}>Link to SOW</label>
          <SowSelect sows={sows} value={form.sow_id} onChange={id => set('sow_id', id)} placeholder="— not matched —" /></div>
        <div>
          <label className={lbl}>Overhead Category {form.sow_id || form.po_id ? <span className="text-gray-300 font-normal normal-case">(not applicable — SOW/PO linked)</span> : null}</label>
          <select className={inp} value={form.overhead_category_id} onChange={e => set('overhead_category_id', e.target.value)}
            disabled={!!(form.sow_id || form.po_id)}>
            <option value="">— select if no SOW/PO —</option>
            {overheadCategories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        {/* The Sage nominal code, chosen here rather than inherited from the
            category. The categories are a reporting taxonomy; the ledger is an
            accounting structure, and one "Vehicles" bucket covers fuel, servicing
            and vehicle insurance across three different codes. The category still
            suggests one, which covers the common case in a single click. */}
        {ledgerOptions.length > 0 && (
          <div>
            <label className={lbl}>Sage Ledger Account</label>
            <SearchSelect
              options={ledgerOptions}
              value={form.sage_ledger_account_id}
              onChange={v => set('sage_ledger_account_id', v)}
              placeholder="Search nominal code or name…"
              emptyLabel={inheritedLabel ? `— use the default: ${inheritedLabel} —` : '— none —'}
              className={inp}
            />
            {/* The supplier's remembered code, exactly as AutoEntry does it:
                the common case is one click and the exception is still easy. */}
            {supplierDefaultLabel && (
              <div className="text-[7px] text-gray-500 mt-0.5 flex items-center gap-1 flex-wrap">
                <span>Usual code for {chosenSupplier?.name || 'this supplier'}:</span>
                <span className="text-gray-700 font-semibold">{supplierDefaultLabel}</span>
                {form.sage_ledger_account_id && form.sage_ledger_account_id !== supplierDefault && (
                  <>
                    <span className="text-amber-700">· overridden for this invoice</span>
                    <button type="button" onClick={() => set('sage_ledger_account_id', '')}
                      className="underline text-gray-500 hover:text-navy">use the usual one</button>
                  </>
                )}
              </div>
            )}
            {inheritedLabel && !supplierDefaultLabel && (
              <div className="text-[7px] text-gray-400 mt-0.5">
                Defaults to <span className="text-gray-600">{inheritedLabel}</span> from the category. Override it here when this invoice belongs somewhere else.
              </div>
            )}
            {/* Setting the default from what is on screen, so it gets remembered
                by using it rather than by being configured somewhere else — and
                so a default that has become wrong can be corrected from the
                invoice that proved it wrong. The wording says which of the two
                is happening, because "remember this" against a supplier that
                already has a different code reads as though it might add one
                rather than replace it. */}
            {form.supplier_id && form.sage_ledger_account_id && form.sage_ledger_account_id !== supplierDefault && (
              <button type="button" onClick={() => onSetSupplierDefault?.(form.supplier_id, form.sage_ledger_account_id)}
                className="text-[7px] mt-0.5 px-1.5 py-0.5 border border-gray-200 rounded text-gray-600 hover:bg-gray-50">
                {supplierDefault
                  ? `Make this the usual code for ${chosenSupplier?.name || 'this supplier'} instead`
                  : `Remember this code for ${chosenSupplier?.name || 'this supplier'}`}
              </button>
            )}
          </div>
        )}

        </>)}

        {/* ── When the money is due ──
            For cash flow. Deliberately not the same as a scheduled payment date:
            this is when the SUPPLIER expects it, which is what makes something
            overdue. */}
        <div className="grid grid-cols-2 gap-2">
          <div><label className={lbl}>Payment Due</label>
            <input className={inp} type="date" value={form.payment_due_date}
              onChange={e => { set('payment_due_date', e.target.value); set('payment_due_source', 'manual'); }} />
            {form.payment_due_source && form.payment_due_source !== 'manual' && (
              <div className="text-[7px] text-gray-400 mt-0.5">
                {form.payment_due_source === 'printed' ? 'Printed on the invoice'
                  : form.payment_due_source === 'from_terms' ? `Worked out from ${form.payment_terms_days}-day terms`
                  : "From this supplier's usual terms, not from the invoice"}
              </div>
            )}
          </div>
          <div><label className={lbl}>Terms (days)</label>
            <input className={inp} type="number" min="0" value={form.payment_terms_days}
              onChange={e => set('payment_terms_days', e.target.value)}
              placeholder={form.payment_terms_text || 'e.g. 30'} />
          </div>
        </div>
        {/* The lines inherit what was chosen for the invoice as a whole: the
            nominal code picked above (or the supplier's/category's default),
            and the VAT rate the invoice totals work out at. Every line can
            still be set differently, which is the whole point of having them. */}
        <LinesEditor lines={lines} setLines={setLines}
          headerNet={form.total_excl_vat} headerVat={form.vat_amount}
          headerLedgerId={form.sage_ledger_account_id || inheritedLedger || ''}
          headerLedgerLabel={
            form.sage_ledger_account_id
              ? ledgerOptions.find(o => o.value === form.sage_ledger_account_id)?.label
              : inheritedLabel
          }
          ledgerOptions={ledgerOptions} inp={inp} lbl={lbl} />

        <div><label className={lbl}>Notes</label>
          <textarea className={inp} rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} /></div>
      </div>
      {creatingSupplier && (
        <CreateSupplierInline supplierId={form.supplier_id}
          onClose={() => setCreatingSupplier(false)}
          onDone={() => { setCreatingSupplier(false); onSageRefresh?.(); }} />
      )}

      <div className="flex flex-col gap-1 pt-2 flex-shrink-0 border-t border-gray-100 mt-2">
        {/* Saying what each button does before it is pressed. None of them puts
            anything in the accounts: invoices have been saved in the belief they
            were on their way there. "Log & check Sage" gets as far as the checks
            and stops, with the payload and any problems on screen and Post left
            to a person. */}
        {isNew && (
          <div className="text-[7px] text-gray-500">
            {isContractor
              ? 'Recorded against the contractor for allocation and approval.'
              : canPrepareSage
                ? 'Logging records the invoice in ops. "Log & check Sage" also runs the Sage checks and shows what would be sent — nothing is posted until you press Post.'
                : 'Logging records the invoice in ops. It does not send it anywhere: posting to Sage is a separate step on the invoice once it is logged.'}
          </div>
        )}
        {/* Why the Sage button is unavailable, said next to the button rather
            than only at the top of a form somebody has scrolled past. */}
        {canPrepareSage && !sageReady && (
          <div className="text-[7px] text-red-700 font-semibold">
            {sageBlockers.map(b => b.key === 'ledger' ? 'Choose a nominal code' : 'Link the supplier to Sage').join(' and ')} before this can be readied for Sage.
          </div>
        )}
        <div className="flex gap-2">
        <button onClick={cancelForm} className="px-2.5 py-1 text-[8px] border border-gray-200 rounded text-gray-500 hover:bg-gray-50">
          Cancel
        </button>
        {isContractor ? (
          <button onClick={() => handleSave('log')} disabled={!!saving || !form.resource_worker_id}
            className="flex-1 px-3 py-1 text-[8px] font-bold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-50"
            title={form.resource_worker_id ? '' : 'Choose the contractor first'}>
            {saving ? 'Saving…' : isNew ? '✓ Log Contractor Invoice' : 'Save Changes'}
          </button>
        ) : (
          <>
            <button onClick={() => handleSave('log')} disabled={!!saving}
              className={`flex-1 px-3 py-1 text-[8px] font-bold rounded disabled:opacity-50 ${canPrepareSage
                ? 'border border-navy text-navy hover:bg-navy/5'
                : 'bg-navy text-white hover:bg-navy/80'}`}>
              {saving === 'log' ? 'Saving…' : !isNew ? 'Save Changes' : canPrepareSage ? 'Log only' : '✓ Log Invoice'}
            </button>
            {/* The ordinary path once Sage is on: log it and have it checked in
                the same press, so reading the invoice and readying it for the
                accounts is one action rather than two screens apart. It is
                unavailable while the supplier has no Sage contact, because
                logging it in that state produces a record that can never post. */}
            {canPrepareSage && (
              <button onClick={() => handleSave('sage')} disabled={!!saving || !sageReady}
                className="flex-1 px-3 py-1 text-[8px] font-bold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-50"
                title={sageReady ? '' : 'The supplier is not linked to a Sage contact yet'}>
                {saving === 'sage' ? 'Checking…' : isNew ? '✓ Log & check Sage' : '✓ Save & check Sage'}
              </button>
            )}
            {/* Only shown while AutoEntry is still in use. A button that is
                switched off server-side and still on screen is a button people
                press and then wonder about. */}
            {isNew && autoentryEnabled && (
              <button onClick={() => handleSave('autoentry')} disabled={!!saving}
                className="flex-1 px-3 py-1 text-[8px] font-bold border border-navy text-navy rounded hover:bg-navy/5 disabled:opacity-50">
                {saving === 'autoentry' ? 'Sending…' : '→ Send to AutoEntry'}
              </button>
            )}
          </>
        )}
        </div>
      </div>
    </div>
  );
}

// ── The document, full size ──────────────────────────────────────────────────
//
// The split pane is right for typing figures off an invoice while looking at
// it, and wrong for actually reading one: at 60% of half a screen an A4 page is
// unreadable without zooming, and "Open PDF" sends the person to another tab
// where the form is not. This is the same document over the top of the screen,
// dismissed with Escape or a click outside, with the form untouched underneath.
function DocumentModal({ att, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    // The page behind must not scroll while this is over it.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  if (!att) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex flex-col p-3" onClick={onClose}>
      <div className="flex items-center gap-2 pb-2 flex-shrink-0" onClick={e => e.stopPropagation()}>
        <span className="text-[11px] font-semibold text-white truncate flex-1">{att.filename}</span>
        {att.url && (
          <a href={att.url} target="_blank" rel="noopener noreferrer"
            className="px-2 py-1 text-[9px] border border-white/30 text-white/80 rounded hover:bg-white/10">
            ↗ Open in a tab
          </a>
        )}
        <button onClick={onClose}
          className="px-2 py-1 text-[9px] font-bold bg-white text-navy rounded hover:bg-gray-100">
          Close (Esc)
        </button>
      </div>
      <div className="flex-1 min-h-0 bg-white rounded overflow-hidden" onClick={e => e.stopPropagation()}>
        <AttachmentPreview att={att} />
      </div>
    </div>
  );
}

// What the extractor is doing, in the order it does it. Wording matters: each
// one is a real step of the request, so somebody waiting can tell a slow read
// from a stuck one.
const EXTRACT_STAGES = [
  'Opening the document…',
  'Reading the invoice…',
  'Finding the figures and the VAT…',
  'Checking for more than one invoice…',
  'Still reading — a long or scanned document takes longer…',
];

// ── Attachment preview (inline, left side) ───────────────────────────────────
function AttachmentPreview({ att }) {
  // An attachment that failed the upload check (pen test AD25996) is kept so the
  // mail is whole, but it is not drawn: this says why, where the preview would be.
  // Only an administrator is given a link to download it (finding AD2, retest).
  if (att?.refused_reason) {
    return (
      <div role="alert" className="flex flex-col items-center justify-center h-full bg-red-50 rounded border border-dashed border-red-200 gap-2 px-6 text-center">
        <span className="text-[9px] font-semibold text-red-700">{att.filename}</span>
        <span className="text-[8px] text-red-700 max-w-xs leading-relaxed">
          Not shown, because it did not pass the check every file in Ops goes through. {att.refused_reason}
        </span>
        <span className="text-[8px] text-gray-500 max-w-xs leading-relaxed">
          If it should be here, ask the sender for a PDF.{' '}
          {att.url
            ? 'Only download it if you know what it is.'
            : 'Only an administrator can download it.'}
        </span>
      </div>
    );
  }
  if (!att?.url) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50 text-[9px] text-gray-400 rounded border border-dashed border-gray-200">
        No preview available
      </div>
    );
  }
  const ct = (att.content_type || '').toLowerCase();
  const fn = (att.filename || '').toLowerCase();
  const isImage = ct.startsWith('image/');
  const isPdf   = ct.includes('pdf') || fn.endsWith('.pdf');

  if (isImage) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-100 rounded overflow-hidden border border-gray-200">
        <img src={att.url} alt={att.filename} className="max-w-full max-h-full object-contain" />
      </div>
    );
  }
  if (isPdf) {
    // No inner header: the pane above already carries the filename, Open and
    // Widen. A second one only took height off the page being read.
    //
    // The overlay is for the case this panel used to fail at silently. A browser
    // that will not render the file downloads it instead and leaves the frame
    // empty, and an empty frame explains nothing. The note sits behind the iframe,
    // so it is what you see if nothing draws over it — and it says what happened
    // and what to do, rather than leaving somebody staring at a blank rectangle
    // wondering whether the invoice is broken.
    return (
      <div className="relative w-full h-full">
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-gray-50 rounded border border-dashed border-gray-200 px-6 text-center">
          <span className="text-2xl">📄</span>
          <span className="text-[9px] font-semibold text-gray-600">{att.filename}</span>
          <span className="text-[8px] text-gray-500 max-w-xs leading-relaxed">
            If this stays blank, your browser has downloaded the file instead of displaying it — check your Downloads,
            or open it in a new tab.
          </span>
          <a href={att.url} target="_blank" rel="noopener noreferrer"
            className="px-3 py-1 text-[8px] bg-navy text-white rounded hover:bg-navy/80">
            Open in a new tab
          </a>
        </div>
        <iframe
          src={att.url}
          className="absolute inset-0 w-full h-full rounded border border-gray-200 bg-white"
          style={{ minHeight: 0 }}
          title={att.filename}
        />
      </div>
    );
  }
  // Not something this panel can draw. Says so, and says what it is.
  return (
    <div className="flex flex-col items-center justify-center h-full bg-gray-50 rounded border border-dashed border-gray-200 gap-2 px-6 text-center">
      <span className="text-2xl">📄</span>
      <span className="text-[9px] font-semibold text-gray-600">{att.filename}</span>
      <span className="text-[8px] text-gray-500 max-w-xs leading-relaxed">
        {ct && ct !== 'application/octet-stream'
          ? `This is a ${ct.split('/').pop().toUpperCase()} file, which cannot be shown here.`
          : 'This file type cannot be shown here.'}
        {' '}Open it to read it — and if it is an invoice, log it with + New.
      </span>
      <a href={att.url} target="_blank" rel="noopener noreferrer"
        className="px-3 py-1 text-[8px] bg-navy text-white rounded hover:bg-navy/80">
        Open file
      </a>
    </div>
  );
}

// ── Email detail (right panel) ────────────────────────────────────────────────
// ── CONTRACTOR INVOICES IN THE INBOX ────────────────────────────────────────
//
// A contractor invoice has an entry here from the moment it is submitted, so
// finance see it with everything else. Until it is approved there is nothing
// to post, and what finance need is when to chase and whom; once it is
// approved its supplier invoice is logged on the entry and posted like any
// other. The stage is worked out on the server (contractorInboxStage).
const CONTRACTOR_STAGE = {
  awaiting_approval: { label: 'Awaiting approval', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  ready_for_sage:    { label: 'Approved · post to Sage', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  in_sage:           { label: 'In Sage', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  paid:              { label: 'Paid', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  rejected:          { label: 'Rejected', cls: 'bg-gray-100 text-gray-500 border-gray-200' },
};

/** Whole days since a timestamp ("2026-11-04 09:30:00" or ISO). */
function daysSince(ts) {
  if (!ts) return null;
  const t = Date.parse(String(ts).includes('T') ? ts : `${String(ts).replace(' ', 'T')}Z`);
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 86400000));
}

const daysLabel = (n) => `${n} day${n === 1 ? '' : 's'}`;
// A week without a decision is when it is worth chasing.
const CHASE_AFTER_DAYS = 7;

function ContractorInvoicePanel({ ci, sageConnected }) {
  const stage = CONTRACTOR_STAGE[ci.stage] || CONTRACTOR_STAGE.awaiting_approval;
  const waited = daysSince(ci.submitted_at);
  const overdue = ci.stage === 'awaiting_approval' && waited != null && waited >= CHASE_AFTER_DAYS;
  const ts = ci.timesheets;
  const billed = Number(ci.days_billed || 0);
  return (
    <div className={`mt-1 rounded border px-2 py-1.5 text-[8px] ${overdue ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'}`}>
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="font-bold text-navy">🏗 Contractor invoice</span>
        <span className="text-gray-700">
          {ci.worker_name || 'Contractor'} · {ci.invoice_number || 'no number'}
          {ci.total_incl_vat != null && <> · {formatMoney(ci.total_incl_vat, 2, ci.currency || 'GBP')} incl. VAT</>}
          {ci.contract_reference && <> · {ci.contract_reference}</>}
        </span>
        <span className={`px-1.5 py-0.5 rounded border font-bold text-[7px] ${stage.cls}`}>{stage.label}</span>
        {/* Approving happens on Ext. Resource Invoices, so before approval that is
            the way forward. After it, posting happens here, on the invoice below:
            a prominent link away would only lead back to this entry. */}
        {ci.stage === 'awaiting_approval' ? (
          <a href={`#resource-invoices/${ci.id}`}
            className="ml-auto px-1.5 py-0.5 rounded border border-navy/30 text-navy font-semibold hover:bg-white whitespace-nowrap">
            Open to approve →
          </a>
        ) : (
          <a href={`#resource-invoices/${ci.id}`} className="ml-auto text-[7px] text-gray-500 hover:underline whitespace-nowrap">
            Details on Ext. Resource Invoices
          </a>
        )}
      </div>
      <div className="mt-0.5 text-gray-700 leading-snug">
        {ci.stage === 'awaiting_approval' && (
          <>
            <span className={overdue ? 'font-bold text-red-700' : ''}>
              Submitted {formatDate(ci.submitted_at)}{waited != null ? `, waiting ${daysLabel(waited)} for approval` : ''}.
            </span>{' '}
            {ts && billed > 0 && (
              ts.pending_days > 0
                ? <>{ts.approved_days} of {billed} days are on approved timesheets. <strong>{ts.pending_days} {ts.pending_days === 1 ? 'day is' : 'days are'} waiting for {ts.approver || 'the timesheet approver'}</strong>, so chase them first.{' '}</>
                : ts.approved_days + 1e-9 >= billed
                  ? <>All {billed} days are on approved timesheets, so it can be approved now.{' '}</>
                  : <>Only {ts.approved_days} of {billed} days are on approved timesheets, and none are waiting: the contractor has not submitted the rest.{' '}</>
            )}
            It cannot go to Sage until it is approved.
          </>
        )}
        {ci.stage === 'ready_for_sage' && (
          <>
            Approved {ci.reviewed_by_name ? `by ${ci.reviewed_by_name}` : 'automatically on submission'}
            {ci.reviewed_at ? ` on ${formatDate(ci.reviewed_at)}` : ''}.{' '}
            {!ci.supplier_invoice_id
              ? 'It is being logged for Sage. Refresh in a moment if it is not below yet.'
              : sageConnected
                ? <>It is logged below for Sage. Press <strong>→ Post to Sage</strong> on it to send it.</>
                : <span className="font-semibold text-red-700">
                    It is logged below, but Sage is not connected, so it cannot be posted yet.{' '}
                    <a href="#sage" className="underline">Connect Sage on the Sage screen →</a>
                  </span>}
            {!ci.has_supplier && (
              <span className="block text-amber-800 font-semibold">
                This contractor has no supplier record, so choose the supplier with Edit on the invoice below before posting.
              </span>
            )}
          </>
        )}
        {ci.stage === 'in_sage' && (
          <>
            In Sage{ci.sage_entered_at ? ` since ${formatDate(ci.sage_entered_at)}` : ''}
            {ci.sage_entered_by_inbox ? ', posted from this inbox' : ci.sage_entered_by_name ? `, entered by hand by ${ci.sage_entered_by_name}` : ''}.
          </>
        )}
        {ci.stage === 'paid' && <>Marked paid{ci.paid_at ? ` on ${formatDate(ci.paid_at)}` : ''}.</>}
        {ci.stage === 'rejected' && (
          <>Rejected{ci.reviewed_by_name ? ` by ${ci.reviewed_by_name}` : ''}{ci.review_notes ? `: ${ci.review_notes}` : '.'}</>
        )}
      </div>
    </div>
  );
}

function EmailDetail({ emailId, suppliers, sows, pos, overheadCategories = [], sageRef, sageConnected, sagePostingEnabled, autoentryEnabled, onSageRefresh, onStatusChange, onClose, onSupplierCreated, resourceWorkers = [] }) {
  const { toast } = useToast();
  const [email,       setEmail]       = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [selectedAtt, setSelectedAtt] = useState(null);  // attachment object
  const [extracting,  setExtracting]  = useState(false);
  const [extractErr,  setExtractErr]  = useState(null);
  const [showForm,    setShowForm]    = useState(false);  // 'new' | 'edit' | false
  const [editInv,     setEditInv]     = useState(null);
  const [formPrefill, setFormPrefill] = useState(null);
  const [isAiProposed, setIsAiProposed] = useState(false);
  const [saving,      setSaving]      = useState(false);
  // Which supplier record the server thinks this is, and why. Held next to the
  // prefill rather than inside it because it is an explanation, not a value.
  const [supplierSuggestion, setSupplierSuggestion] = useState(null);
  // Set when one file turned out to hold several invoices: the list, plus which
  // of them have already been logged in this sitting.
  const [foundInvoices, setFoundInvoices] = useState(null);
  // The invoice that was saved with "Log & check Sage": its Sage panel runs the
  // checks on its own as soon as the list comes back, so the sequence is read
  // the invoice, save it, post it.
  const [sagePrepareFor, setSagePrepareFor] = useState(null);
  // Give the document the whole pane. Some invoices are simply not readable at
  // 60% of half a screen, and opening them in a new tab loses the form.
  const [docWide, setDocWide] = useState(false);
  const [docModal, setDocModal] = useState(false);
  // Reading a PDF takes tens of seconds. A spinner that says nothing for that
  // long reads as a hang, so the stages the request actually goes through are
  // stepped from a timer — honest about being an estimate, and it stops on the
  // last one rather than pretending to finish.
  const [extractStage, setExtractStage] = useState(0);
  // Whether what is on screen came back from the store or was read just now.
  const [lastExtract, setLastExtract] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`procurement/emails/${emailId}`)
      .then(d => {
        setEmail(d);
        setLoading(false);
        // Auto-select the first attachment that still needs doing, not simply
        // the first one. On a two-invoice email the first is usually already
        // logged, and opening on it is how the second one gets missed.
        if (!selectedAtt && d?.attachments?.length > 0) {
          const docs = d.attachments.filter(isDocumentAttachment);
          const done = new Set((d.invoices || []).map(i => i.attachment_id).filter(Boolean));
          const next = docs.find(a => !a.not_an_invoice && !done.has(a.id));
          setSelectedAtt(next || docs[0] || null);
        }
      })
      .catch(() => setLoading(false));
  }, [emailId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // The stage ticker. Purely cosmetic and deliberately so: the request is one
  // call with no progress events to report, so this shows what it is doing
  // rather than claiming how far through it is. It stops on the final stage
  // instead of running to 100% and sitting there, which is the thing that makes
  // a fake progress bar feel like a lie.
  useEffect(() => {
    if (!extracting) return;
    const t = setInterval(() => setExtractStage(v => Math.min(v + 1, EXTRACT_STAGES.length - 1)), 2600);
    return () => clearInterval(t);
  }, [extracting]);

  const updateStatus = async (status) => {
    setSaving(true);
    await api.put(`procurement/emails/${emailId}`, { status }).catch(() => {});
    setSaving(false);
    onStatusChange(emailId, status);
    load();
  };

  // One extracted invoice → the form, prefilled. Shared by the single-invoice
  // path and by picking one out of a multi-invoice document.
  const openExtractedInvoice = (att, inv, suggestion) => {
    setSupplierSuggestion(suggestion || null);
    setFormPrefill({
      attachment_id:  att.id,
      invoice_number: inv.invoice_number || '',
      invoice_date:   inv.invoice_date   || '',
      supplier_name:  inv.supplier_name  || '',
      // What the extractor read the document AS. A credit note posts to Sage
      // as a purchase credit note, which subtracts the VAT instead of adding
      // it — so the reading has to reach the form, where it can be corrected.
      document_type:  inv.document_kind === 'credit_note' ? 'credit_note' : 'invoice',
      // The supplier record chosen for this company last time. Filled in so
      // the common case is nothing to do; the note under the field says
      // where it came from, and it can be changed.
      supplier_id:    suggestion?.supplier_id || '',
      total_excl_vat: inv.total_excl_vat ?? '',
      vat_amount:     inv.vat_amount     ?? '',
      total_incl_vat: inv.total_incl_vat ?? '',
      currency:       inv.currency       || 'GBP',
      // Read off the document, so it counts as evidence for the reclaim.
      supplier_vat_number: inv.supplier_vat_number || '',
      vat_treatment:       inv.vat_treatment       || '',
      payment_due_date:    inv.payment_due_date    || '',
      payment_terms_days:  inv.payment_terms_days  ?? '',
      payment_terms_text:  inv.payment_terms_text  || '',
      // For a contractor's invoice: the work period and the days billed, which
      // the contract's days used are counted from.
      period_from:         inv.period_from         || '',
      period_to:           inv.period_to           || '',
      labour_days:         inv.days_billed         ?? '',
      // Only kept when the extraction says the lines reconcile. A set that
      // does not add up is worse than no lines: it looks authoritative and
      // then blocks the post.
      lines: Array.isArray(inv.lines) ? inv.lines.map(l => ({ ...l, source: 'ai' })) : [],
    });
    setIsAiProposed(true);
    setShowForm('new');
  };

  const autoExtract = async (att, { force = false } = {}) => {
    if (extracting) return;
    setSelectedAtt(att);
    setExtracting(true);
    setExtractStage(0);
    setExtractErr(null);
    setShowForm(false);
    setIsAiProposed(false);
    setSupplierSuggestion(null);
    setFoundInvoices(null);
    try {
      const res = await api.post(`procurement/attachments/${att.id}/ai-extract`, { force });
      setLastExtract(res?.cached ? { cached: true, at: res.extracted_at } : { cached: false });
      const found = Array.isArray(res.invoices) ? res.invoices : (res.extracted ? [res.extracted] : []);
      if (found.length > 1) {
        // Several invoices in the one file — a month scanned in a batch, or a
        // statement with its invoices behind it. The old extractor returned a
        // single object here and quietly lost the rest, which is why this now
        // asks rather than guessing.
        setFoundInvoices({ attachment: att, invoices: found, suggestion: res.supplier_suggestion || null, logged: [] });
        setShowForm(false);
      } else if (found.length === 1) {
        openExtractedInvoice(att, found[0], res.supplier_suggestion);
      } else {
        // Extraction returned 200 but no data — open blank manual form
        setExtractErr('AI could not identify invoice fields. Form opened for manual entry.');
        setFormPrefill({ attachment_id: att.id });
        setShowForm('new');
      }
    } catch (e) {
      // Any failure — open the manual form so there is no dead end, but say what
      // actually went wrong. This used to print one sentence for every cause:
      // a document Claude declined, a request the API refused, a file too big,
      // an expired key and a genuinely unreadable scan all read as "could not
      // auto-read this document", and the only way to tell them apart was the
      // browser console. The server already sends a specific reason; it was
      // being thrown away here.
      // `error` on this endpoint is a code ("OCR_FAILED"); `message` is the
      // sentence written for a person to read.
      setExtractErr(e.data?.message || e.message || 'Could not auto-read this document — please fill in the fields manually.');
      setFormPrefill({ attachment_id: att.id });
      setShowForm('new');
    } finally {
      setExtracting(false);
      setExtractStage(0);
    }
  };

  // Remembering a supplier's usual nominal code, set from the one on screen so
  // it is learned by being used rather than configured somewhere else.
  const setSupplierDefaultLedger = async (supplierId, ledgerId) => {
    try {
      await api.put(`sage/map-supplier/${supplierId}`, { default_sage_ledger_account_id: ledgerId });
      // Said out loud. A silent success on a button pressed once, whose effect is
      // only visible on the NEXT invoice from that supplier, is indistinguishable
      // from a button that does nothing — which is exactly how this was reported
      // as not working.
      toast.success("Saved as this supplier's usual nominal code.");
    } catch (e) {
      toast.error(e.message || 'Could not save that as the default.');
    }
    onSageRefresh?.();
  };

  // The EU goods-or-services answer, remembered against the supplier. Separate
  // from the nominal code because they are set from different places on the form
  // and a shared handler would have to guess which field was meant.
  const setSupplierEuDefault = async (supplierId, choice) => {
    try {
      await api.put(`sage/map-supplier/${supplierId}`, { eu_goods_services: choice });
      toast.success(`Saved: purchases from this supplier are ${choice}.`);
    } catch (e) {
      toast.error(e.message || 'Could not save that as the default.');
    }
    onSageRefresh?.();
  };

  const saveInvoice = async (form) => {
    const { lines, ...header } = form;
    // A contractor invoice is a different record in a different table with its
    // own approval and allocation. That is why choosing between them used to be
    // a fork before the form; it is now a switch on the form, and the fork
    // happens here where it belongs.
    if (form.invoice_type === 'contractor') {
      const res = await api.post('resource-invoices', {
        resource_worker_id: form.resource_worker_id,
        contract_id: form.contract_id || null,
        labour_days: form.labour_days !== '' && form.labour_days != null ? parseFloat(form.labour_days) : null,
        invoice_number: form.invoice_number || null,
        invoice_date: form.invoice_date || null,
        period_from: form.period_from || null,
        period_to: form.period_to || null,
        total_amount: form.total_incl_vat !== '' && form.total_incl_vat != null
          ? parseFloat(form.total_incl_vat)
          : (form.total_excl_vat !== '' && form.total_excl_vat != null ? parseFloat(form.total_excl_vat) : null),
        notes: form.notes || null,
        source_email_id: emailId,
        // The document it was read from, kept on the contractor invoice.
        attachment_id: form.attachment_id || null,
      });
      onStatusChange(emailId, 'matched');
      // Saved either way — the duplicate check is a warning, not a refusal — so
      // it is said out loud rather than left in a panel nobody reopens.
      if (res?.duplicate_warning) toast.error(`Saved, but: ${res.duplicate_warning}`);
      else toast.success('Contractor invoice logged.');
      setShowForm(false);
      setEditInv(null);
      setFormPrefill(null);
      setIsAiProposed(false);
      load();
      return;
    }
    if (editInv?.id) {
      await api.put(`procurement/supplier-invoices/${editInv.id}`, header);
      // Lines are replaced as a set through their own endpoint. A rejection here
      // means they do not reconcile with the header, which the person needs told
      // rather than left to find at post time.
      try { await api.put(`procurement/supplier-invoices/${editInv.id}/lines`, { lines: lines || [] }); }
      catch (e) { toast.error(e.message || 'The lines were not saved.'); }
      if (form.prepare_sage) setSagePrepareFor(editInv.id);
    } else {
      const res = await api.post('procurement/supplier-invoices', form);
      onStatusChange(emailId, 'invoice_received');
      // The point of "Log & check Sage": the invoice that was just saved is the
      // one whose Sage checks run, without anybody having to find it again in
      // the list underneath. Only this one — the preview does a live duplicate
      // lookup against Sage, and firing that for every invoice on screen would
      // be one request per row.
      if (form.prepare_sage && res?.id) setSagePrepareFor(res.id);
      // Tick this one off the multi-invoice list so it is obvious which of the
      // invoices in the file still need logging.
      setFoundInvoices(prev => prev && ({
        ...prev,
        logged: [...prev.logged, `${form.invoice_number || ''}|${form.total_incl_vat ?? ''}`],
      }));
      // Surface the AutoEntry forward outcome — previously failures were silent.
      if (form.send_autoentry) {
        if (res?.autoentry?.ok) toast.success('Invoice sent to AutoEntry');
        else toast.error(res?.autoentry?.error || 'Could not send to AutoEntry — the invoice was still logged.');
      }
    }
    setShowForm(false);
    setEditInv(null);
    setFormPrefill(null);
    setIsAiProposed(false);
    load();
  };

  const deleteInvoice = async (id) => {
    if (!confirm('Delete this invoice record?')) return;
    await api.delete(`procurement/supplier-invoices/${id}`);
    load();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-[9px] text-gray-400">Loading…</div>
    );
  }
  if (!email) {
    return (
      <div className="flex items-center justify-center h-full text-[9px] text-red-400">Failed to load email</div>
    );
  }

  // Does this email come from somebody on the contractor register? If so the
  // invoice on it is almost certainly theirs, and the form opens on the
  // contractor side already filled in rather than asking.
  const contractorMatch = resourceWorkers.find(
    w => w.email && w.email.toLowerCase() === (email.sender_email || '').toLowerCase()) || null;

  // Only PDFs can be AI-extracted (images filtered out entirely)
  // Filename-aware: an invoice attached as octet-stream is still an invoice, and
  // this test deciding otherwise is why Extract Invoice went missing on some of
  // them.
  const canExtract = att => attachmentIsPdf(att);
  // Only show document attachments (not inline images like email logos/signatures)
  const docAttachments = (email.attachments || []).filter(isDocumentAttachment);
  const hasAttachments = docAttachments.length > 0;

  // Which attachment produced which invoice. Derived rather than stored, so it
  // can never disagree with the invoices that actually exist.
  const attachmentInvoices = new Map();
  for (const inv of email.invoices || []) {
    if (!inv.attachment_id) continue;
    if (!attachmentInvoices.has(inv.attachment_id)) attachmentInvoices.set(inv.attachment_id, []);
    attachmentInvoices.get(inv.attachment_id).push(inv);
  }
  const outstandingAtts = docAttachments.filter(a => !a.not_an_invoice && !attachmentInvoices.has(a.id));
  // Logged, but the ledger has not got it yet. A separate count from "to do",
  // because it is a different job: one needs reading, this one needs posting.
  const awaitingSageAtts = docAttachments.filter(a => {
    const invs = attachmentInvoices.get(a.id) || [];
    return invs.length > 0 && invs.some(inv => !isSettled(inv));
  });

  // The contractor invoices behind this entry, and whether the entry was made
  // for a portal invoice (no email behind it). A made entry is logged for Sage
  // by itself at approval and its status follows the invoice, so the controls
  // for logging by hand and moving it between tabs are not offered on it.
  const contractorInvoices = email.contractor_invoices || [];
  const isPortalEntry = contractorInvoices.some(c => c.portal_entry);

  const toggleNotInvoice = async (att, on) => {
    await api.put(`procurement/attachments/${att.id}/not-invoice`, { not_an_invoice: on }).catch(() => {});
    load();
  };
  // Show split pane if an attachment is selected for viewing
  const showSplit = !!selectedAtt;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* ── Email header ──────────────────────────────────────────────────────
          One line, not five. This bar used to run to a third of the screen —
          status pill on its own row, subject, sender, a "Mark as:" row, then a
          stacked list of attachments — and every pixel of it came out of the
          document underneath. An accounts team reads the invoice; the email it
          arrived on is context, not the work. */}
      <div className="flex-shrink-0 px-3 py-1.5 border-b border-gray-100 bg-gray-50">
        <div className="flex items-center gap-2">
          <StatusPill status={email.status} />
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-bold text-navy leading-tight truncate" title={email.subject}>
              {email.subject}
            </div>
            <div className="text-[7px] text-gray-400 truncate">
              <span className="text-gray-600">{email.sender_name}</span>
              {email.sender_email ? ` <${email.sender_email}>` : ''} · {formatDateTime(email.received_at)}
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {!isPortalEntry && email.status !== 'not_applicable' && (
              <button onClick={() => updateStatus('not_applicable')} disabled={saving}
                className="px-1.5 py-0.5 text-[7px] border border-slate-200 text-slate-500 rounded hover:bg-slate-50 disabled:opacity-40"
                title="Nothing on this email needs logging">
                N/A
              </button>
            )}
            {!isPortalEntry && email.status !== 'unprocessed' && (
              <button onClick={() => updateStatus('unprocessed')} disabled={saving}
                className="px-1.5 py-0.5 text-[7px] border border-gray-200 text-gray-400 rounded hover:bg-gray-50 disabled:opacity-40"
                title="Put it back on the unprocessed list">
                ← Unprocessed
              </button>
            )}
            <button onClick={onClose}
              className="w-5 h-5 rounded-full border border-gray-200 bg-white text-gray-400 hover:bg-gray-100 flex items-center justify-center text-[9px]">
              ✕
            </button>
          </div>
        </div>

        {/* ── Attachments, as a strip ──
            The state each one carries is the point — two invoice PDFs on one
            email look exactly like one otherwise, and missing the second is
            silent. Whether an attachment HAS produced an invoice is derived from
            the invoices themselves rather than stored, so it cannot go stale.
            What changed is the shape: a row of chips instead of a stacked list,
            because on a two-attachment email the list was six lines of chrome. */}
        {hasAttachments && (
          <div className="flex items-center gap-1 mt-1 flex-wrap">
            {docAttachments.map(att => {
              const isActive = selectedAtt?.id === att.id;
              const logged = attachmentInvoices.get(att.id) || [];
              const dismissed = !!att.not_an_invoice;
              // Green only once the ledger has it. Logged-but-not-posted gets its
              // own colour and its own mark, because a tick against an invoice
              // that never reached Sage is the thing that made this page lie.
              const inLedger = logged.length > 0 && logged.every(isSettled);
              const pending = logged.length > 0 && !inLedger;
              return (
                <div key={att.id}
                  className={`flex items-center gap-1 pl-1.5 pr-1 py-0.5 rounded border text-[7px] max-w-[15rem] ${
                    isActive ? 'border-navy bg-white ring-1 ring-navy/20'
                      : inLedger ? 'border-emerald-200 bg-emerald-50'
                      : pending ? 'border-amber-300 bg-amber-50'
                      : dismissed ? 'border-gray-200 bg-gray-50'
                      : 'border-amber-300 bg-amber-50'}`}>
                  <span className="flex-shrink-0" title={pending ? 'Logged, but not in Sage yet' : ''}>
                    {inLedger ? '✓' : pending ? '◐' : dismissed ? '—' : '●'}
                  </span>
                  <button onClick={() => { setSelectedAtt(att); setShowForm(false); setExtractErr(null); }}
                    className={`truncate ${isActive ? 'text-navy font-bold' : 'text-gray-600 hover:text-navy'}`}
                    title={att.filename}>
                    {att.filename}
                  </button>
                  {logged.length > 0 && (
                    <span className={`flex-shrink-0 ${inLedger ? 'text-emerald-700' : 'text-amber-800 font-semibold'}`}>
                      {logged.length === 1
                        ? `${logged[0].invoice_number || 'logged'}${inLedger ? '' : ' · not in Sage'}`
                        : `${logged.length}${inLedger ? '' : ' · not in Sage'}`}
                    </span>
                  )}
                  {!logged.length && !isPortalEntry && (
                    <button onClick={() => toggleNotInvoice(att, !dismissed)}
                      className="flex-shrink-0 px-1 rounded text-gray-400 hover:text-gray-700 hover:bg-white"
                      title={dismissed ? 'Put it back on the list' : 'A statement, covering letter or terms — not an invoice'}>
                      {dismissed ? '↺' : '✕'}
                    </button>
                  )}
                </div>
              );
            })}
            {/* "All done" now means what it says: every attachment is either not
                an invoice, or is an invoice the ledger has. Anything logged and
                not yet posted is called out, because that was the state reading
                as finished. */}
            {!isPortalEntry && <span className={`text-[7px] font-bold px-1.5 py-0.5 rounded flex-shrink-0 ${
              outstandingAtts.length || awaitingSageAtts.length
                ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>
              {outstandingAtts.length
                ? `${outstandingAtts.length} to read`
                : awaitingSageAtts.length
                  ? `${awaitingSageAtts.length} not in Sage`
                  : 'all done · in Sage'}
            </span>}
          </div>
        )}
        {contractorInvoices.map(ci => <ContractorInvoicePanel key={ci.id} ci={ci} sageConnected={sageConnected} />)}
      </div>

      {docModal && <DocumentModal att={selectedAtt} onClose={() => setDocModal(false)} />}

      {/* ── Main body: split pane or single pane ── */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        {showSplit ? (
          <>
            {/* Left: the document.
                Sixty per cent of the width and all of the height under a
                one-line header. An invoice is read, not glanced at, and at 52%
                under a header five rows deep there was barely a third of an A4
                page on screen — the figures being typed into the form on the
                right were being read through a letterbox. `Widen` gives it the
                whole pane for the ones that need it. */}
            <div className="flex flex-col overflow-hidden border-r border-gray-100"
              style={{ width: docWide ? '100%' : '60%', minWidth: 0 }}>
              <div className="flex items-center justify-between px-3 py-1 border-b border-gray-100 bg-gray-50 flex-shrink-0">
                <span className="text-[8px] font-semibold text-gray-500 truncate">{selectedAtt.filename}</span>
                <div className="flex gap-1 flex-shrink-0 ml-2">
                  <button onClick={() => setDocModal(true)}
                    className="px-2 py-0.5 text-[7px] font-bold border border-navy text-navy rounded hover:bg-navy/5"
                    title="Read it full size (Esc to close)">
                    ⛶ Full size
                  </button>
                  <button onClick={() => setDocWide(v => !v)}
                    className="px-2 py-0.5 text-[7px] border border-gray-200 text-gray-500 rounded hover:bg-gray-50"
                    title={docWide ? 'Show the invoice form again' : 'Give the document the full width'}>
                    {docWide ? '⇥ Show form' : '⇤ Widen'}
                  </button>
                  {selectedAtt.url && (
                    <a href={selectedAtt.url} target="_blank" rel="noopener noreferrer"
                      className="px-2 py-0.5 text-[7px] border border-gray-200 text-gray-500 rounded hover:bg-gray-50">
                      ↗ Open
                    </a>
                  )}
                  {/* Extracting again from an attachment that has already
                      produced an invoice does not update that invoice: it opens
                      a blank form that saves a second one. So on a done
                      attachment the primary button goes away, and what is left
                      is an explicit re-read that says what it will do. The
                      prominent button becomes the one that moves the email
                      forward, which is the next attachment. */}
                  {!isPortalEntry && canExtract(selectedAtt) && !(attachmentInvoices.get(selectedAtt.id) || []).length && (
                    <>
                      <button onClick={() => autoExtract(selectedAtt)} disabled={extracting}
                        className="px-2 py-0.5 text-[7px] font-bold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-50 flex items-center gap-1">
                        {extracting ? (
                          <><span className="animate-spin inline-block w-2 h-2 border border-white border-t-transparent rounded-full" />Reading…</>
                        ) : selectedAtt.ai_extracted_at ? '✦ Show what was read' : '✦ Extract Invoice'}
                      </button>
                      {/* Already read once. Showing it again is free and
                          instant; reading it again costs a call and can come
                          back subtly different, so it is a separate button
                          rather than what the main one silently does. */}
                      {selectedAtt.ai_extracted_at && (
                        <button onClick={() => autoExtract(selectedAtt, { force: true })} disabled={extracting}
                          className="px-2 py-0.5 text-[7px] border border-gray-200 text-gray-500 rounded hover:bg-gray-50 disabled:opacity-50"
                          title="Discard what was read before and read the document again">
                          ↻ Read again
                        </button>
                      )}
                    </>
                  )}
                  {!isPortalEntry && canExtract(selectedAtt) && (attachmentInvoices.get(selectedAtt.id) || []).length > 0 && (
                    <button
                      onClick={() => {
                        if (!confirm('This attachment has already been logged as an invoice.\n\n'
                          + 'Reading it again does NOT update that invoice — it opens a new form, and saving it '
                          + 'would create a second invoice for the same document.\n\n'
                          + 'To correct the one that exists, close this and press Edit on it instead.\n\n'
                          + 'Read it again anyway?')) return;
                        autoExtract(selectedAtt, { force: true });
                      }}
                      disabled={extracting}
                      className="px-2 py-0.5 text-[7px] border border-gray-200 text-gray-400 rounded hover:bg-gray-50 hover:text-gray-600 disabled:opacity-50">
                      {extracting ? 'Reading…' : 'Read again'}
                    </button>
                  )}
                  {(() => {
                    const next = outstandingAtts.find(a => a.id !== selectedAtt.id);
                    if (!next) return null;
                    return (
                      <button onClick={() => { setSelectedAtt(next); setShowForm(false); setExtractErr(null); }}
                        className="px-2 py-0.5 text-[7px] font-bold bg-amber-500 text-amber-950 rounded hover:bg-amber-600 flex items-center gap-1"
                        title={next.filename}>
                        Next to do ({outstandingAtts.length}) →
                      </button>
                    );
                  })()}
                </div>
              </div>
              {/* What this attachment already produced, stated before anybody
                  reaches for the extract button rather than after. */}
              {lastExtract?.cached && !extracting && (
                <div className="mx-1 mt-1 px-2 py-1 text-[7px] text-gray-600 bg-gray-50 border border-gray-200 rounded flex-shrink-0">
                  Showing what was read from this document
                  {lastExtract.at ? ` on ${formatDateTime(lastExtract.at)}` : ' earlier'}. Press "Read again" to read it afresh.
                </div>
              )}
              {(attachmentInvoices.get(selectedAtt.id) || []).length > 0 && (
                <div className="mx-1 mt-1 px-2 py-1 text-[7px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded flex-shrink-0">
                  ✓ Already logged as {(attachmentInvoices.get(selectedAtt.id) || [])
                    .map(i => i.invoice_number || 'an invoice').join(', ')}.
                  {' '}Use Edit on the invoice to change it. Reading it again would create a second one.
                </div>
              )}
              {/* One file, several invoices. The extractor used to return a
                  single object here and the rest of the document was lost
                  without anybody being told; now it says what it found and each
                  one is logged in turn. */}
              {foundInvoices && foundInvoices.attachment?.id === selectedAtt.id && (
                <div className="mx-1 mt-1 px-2 py-1.5 text-[8px] bg-blue-50 border border-blue-200 rounded flex-shrink-0">
                  <div className="font-bold text-blue-900 mb-1">
                    {foundInvoices.invoices.length} invoices found in this file — log them one at a time.
                  </div>
                  <div className="space-y-1">
                    {foundInvoices.invoices.map((inv, i) => {
                      const key = `${inv.invoice_number || ''}|${inv.total_incl_vat ?? ''}`;
                      const done = foundInvoices.logged.includes(key);
                      const pages = inv.page_from
                        ? (inv.page_to && inv.page_to !== inv.page_from ? `pp. ${inv.page_from}–${inv.page_to}` : `p. ${inv.page_from}`)
                        : null;
                      return (
                        <div key={i} className={`flex items-center justify-between gap-2 px-1.5 py-1 rounded border ${done ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-blue-200'}`}>
                          <div className="min-w-0">
                            <span className="font-semibold text-gray-800">{inv.invoice_number || 'No number'}</span>
                            {/* Said before it is logged: this is where somebody
                                chooses what to log, so it is where the kind of
                                document matters most. */}
                            {inv.document_kind === 'credit_note' && (
                              <span className="ml-1 text-[7px] font-bold px-1 py-0.5 rounded bg-red-100 text-red-700">↩ Credit note</span>
                            )}
                            {inv.supplier_name && <span className="text-gray-500"> · {inv.supplier_name}</span>}
                            {inv.invoice_date && <span className="text-gray-400"> · {inv.invoice_date}</span>}
                            {inv.total_incl_vat != null && <span className="text-gray-700"> · £{Number(inv.total_incl_vat).toFixed(2)}</span>}
                            {pages && <span className="text-gray-400"> · {pages}</span>}
                          </div>
                          {done ? (
                            <span className="text-emerald-700 font-bold whitespace-nowrap">✓ logged</span>
                          ) : (
                            <button
                              onClick={() => openExtractedInvoice(foundInvoices.attachment, inv, foundInvoices.suggestion)}
                              className="px-1.5 py-0.5 rounded bg-navy text-white font-semibold whitespace-nowrap hover:bg-navy/80">
                              Log this one
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <button onClick={() => setFoundInvoices(null)} className="mt-1 text-[7px] text-blue-700 underline">
                    Dismiss
                  </button>
                </div>
              )}
              {extractErr && (
                <div className="mx-1 mt-1 px-2 py-1 text-[7px] text-amber-800 bg-amber-50 border border-amber-200 rounded flex-shrink-0">
                  ⚠ {extractErr}
                </div>
              )}
              {extracting && (
                <div className="mx-1 mt-1 px-2 py-1.5 rounded bg-blue-50 border border-blue-200 flex-shrink-0">
                  <div className="flex items-center gap-2 text-[8px] text-blue-800 font-semibold">
                    <span className="animate-spin w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full" />
                    {EXTRACT_STAGES[extractStage]}
                  </div>
                  {/* Discrete steps rather than a percentage. There is no
                      percentage to report, and inventing one is how a progress
                      bar stops being believed. */}
                  <div className="flex gap-0.5 mt-1">
                    {EXTRACT_STAGES.map((_, i) => (
                      <div key={i} className={`h-0.5 flex-1 rounded ${i <= extractStage ? 'bg-blue-500' : 'bg-blue-200'}`} />
                    ))}
                  </div>
                </div>
              )}
              <div className="flex-1 p-1 min-h-0">
                <AttachmentPreview att={selectedAtt} />
              </div>
            </div>

            {/* Right: invoice form or invoice list */}
            <div className={`flex-col overflow-hidden ${docWide ? 'hidden' : 'flex'}`} style={{ width: '40%', minWidth: 0 }}>
              <div className="flex-shrink-0 px-3 py-1.5 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                <span className="text-[8px] font-bold text-gray-500 uppercase">
                  {showForm ? (editInv ? 'Edit Invoice' : 'Log Invoice') : 'Logged Invoices'}
                </span>
                {!showForm && !isPortalEntry && (
                  <button onClick={() => { setFormPrefill(null); setEditInv(null); setIsAiProposed(false); setShowForm('new'); }}
                    className="px-2 py-0.5 text-[7px] font-bold bg-navy text-white rounded hover:bg-navy/80">
                    + New
                  </button>
                )}
              </div>
              <div className="flex-1 overflow-y-auto p-3 min-h-0">
                {showForm ? (
                  <InvoiceForm
                    emailId={email.id}
                    attachmentId={selectedAtt?.id || null}
                    initial={editInv || formPrefill}
                    suppliers={suppliers}
                    sows={sows}
                    pos={pos}
                    overheadCategories={overheadCategories}
                    sageRef={sageRef}
                    onSetSupplierDefault={setSupplierDefaultLedger}
                    onSetSupplierEuDefault={setSupplierEuDefault}
                    onSageRefresh={onSageRefresh}
                    autoentryEnabled={autoentryEnabled}
                    sagePostingEnabled={sageConnected && sagePostingEnabled}
                    resourceWorkers={resourceWorkers}
                    contractorMatch={contractorMatch}
                    senderEmail={email.sender_email}
                    isAiProposed={isAiProposed && !editInv}
                    supplierSuggestion={editInv ? null : supplierSuggestion}
                    hasAttachment={!!selectedAtt}
                    onSave={saveInvoice}
                    onSupplierCreated={onSupplierCreated}
                    onCancel={() => { setShowForm(false); setEditInv(null); setFormPrefill(null); setIsAiProposed(false); }}
                  />
                ) : (
                  <InvoiceList
                    invoices={email.invoices || []}
                    portalEntry={isPortalEntry}
                    sageConnected={sageConnected}
                    autoCheckFor={sagePrepareFor}
                    onAutoChecked={() => setSagePrepareFor(null)}
                    onChanged={load}
                    onEdit={async inv => {
                      const ls = await api.get(`procurement/supplier-invoices/${inv.id}/lines`).catch(() => []);
                      setEditInv({ ...inv, lines: ls || [] });
                      setFormPrefill(null); setIsAiProposed(false); setShowForm('edit');
                    }}
                    onDelete={deleteInvoice}
                  />
                )}
              </div>
            </div>
          </>
        ) : (
          /* Single pane — no attachment selected yet */
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {/* Body — full HTML if available, else plain text preview */}
            {(email.body_html || email.body_preview) && (
              <div className="bg-white border border-gray-100 rounded overflow-hidden">
                <div className="text-[7px] text-gray-400 uppercase font-bold px-3 pt-2 pb-1 border-b border-gray-100 bg-gray-50">Email Body</div>
                {email.body_html ? (
                  <iframe
                    srcDoc={email.body_html}
                    sandbox="allow-same-origin"
                    className="w-full border-0"
                    style={{ minHeight: 320, height: 420 }}
                    title="Email body"
                    onLoad={e => {
                      try {
                        const doc = e.target.contentDocument;
                        if (doc) e.target.style.height = Math.min(doc.body.scrollHeight + 24, 600) + 'px';
                      } catch { /* cross-origin sandbox */ }
                    }}
                  />
                ) : (
                  <div className="text-[9px] text-gray-600 whitespace-pre-wrap px-3 py-2">{email.body_preview}</div>
                )}
              </div>
            )}
            {/* Logged invoices.
                The way in lives here rather than in the email header: an email
                with no attachment on it is the only route that needs it, and a
                button in the header was one more thing between the person and
                the document on every other email. */}
            {!showForm && (
              <div className="flex items-center justify-between">
                <span className="text-[8px] font-bold text-gray-500 uppercase">Logged Invoices</span>
                {!isPortalEntry && (
                  <button onClick={() => { setFormPrefill(null); setEditInv(null); setIsAiProposed(false); setShowForm('new'); }}
                    className="px-2 py-0.5 text-[7px] font-bold bg-navy text-white rounded hover:bg-navy/80">
                    + Log Invoice
                  </button>
                )}
              </div>
            )}
            {showForm ? (
              <InvoiceForm
                emailId={email.id}
                attachmentId={null}
                initial={editInv || formPrefill}
                suppliers={suppliers}
                sows={sows}
                pos={pos}
                overheadCategories={overheadCategories}
                sageRef={sageRef}
                onSetSupplierDefault={setSupplierDefaultLedger}
                onSetSupplierEuDefault={setSupplierEuDefault}
                onSageRefresh={onSageRefresh}
                autoentryEnabled={autoentryEnabled}
                sagePostingEnabled={sageConnected && sagePostingEnabled}
                resourceWorkers={resourceWorkers}
                contractorMatch={contractorMatch}
                senderEmail={email.sender_email}
                isAiProposed={false}
                hasAttachment={false}
                onSave={saveInvoice}
                onSupplierCreated={onSupplierCreated}
                onCancel={() => { setShowForm(false); setEditInv(null); setFormPrefill(null); }}
              />
            ) : (
              <InvoiceList
                invoices={email.invoices || []}
                portalEntry={isPortalEntry}
                sageConnected={sageConnected}
                autoCheckFor={sagePrepareFor}
                onAutoChecked={() => setSagePrepareFor(null)}
                onChanged={load}
                onEdit={async inv => {
                  const ls = await api.get(`procurement/supplier-invoices/${inv.id}/lines`).catch(() => []);
                  setEditInv({ ...inv, lines: ls || [] });
                  setShowForm('edit');
                }}
                onDelete={deleteInvoice}
              />
            )}
            {!hasAttachments && (
              <div className="text-[8px] text-gray-400 text-center py-4">No document attachments on this email</div>
            )}
          </div>
        )}
      </div>

    </div>
  );
}

// ── The invoice's history ───────────────────────────────────────────────────
//
// One timeline, assembled from the invoice row, the audit trail and the Sage
// call log. Nothing here is a separate record that could drift out of step with
// what actually happened: it is derived from the records themselves, so it
// cannot claim something that did not occur.
const EVENT_STYLE = {
  created:      { dot: 'bg-gray-400',    text: 'text-gray-700' },
  edit:         { dot: 'bg-gray-300',    text: 'text-gray-600' },
  vat:          { dot: 'bg-blue-400',    text: 'text-blue-800' },
  autoentry:    { dot: 'bg-indigo-400',  text: 'text-indigo-800' },
  override:     { dot: 'bg-amber-400',   text: 'text-amber-800' },
  sage:         { dot: 'bg-navy',        text: 'text-navy' },
  sage_error:   { dot: 'bg-red-500',     text: 'text-red-700' },
  posted:       { dot: 'bg-emerald-500', text: 'text-emerald-800' },
  verified:     { dot: 'bg-emerald-600', text: 'text-emerald-800' },
  verify_issue: { dot: 'bg-amber-500',   text: 'text-amber-800' },
};

const STATE_LINE = {
  in_ops:            { text: 'Logged in ops. Not sent anywhere yet.',                        cls: 'bg-gray-50 border-gray-200 text-gray-700' },
  with_autoentry:    { text: 'Sent to AutoEntry. Ops will not post it as well.',             cls: 'bg-indigo-50 border-indigo-200 text-indigo-800' },
  posted_unverified: { text: 'Posted to Sage, but not checked since. Press Verify.',         cls: 'bg-amber-50 border-amber-200 text-amber-800' },
  differences:       { text: 'In Sage, but what is there does not match what was sent.',     cls: 'bg-red-50 border-red-200 text-red-800' },
  confirmed:         { text: 'Confirmed in Sage: read back and it matches.',                 cls: 'bg-emerald-50 border-emerald-200 text-emerald-800' },
};

function InvoiceHistory({ invoiceId, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState(null);

  const load = useCallback(async () => {
    try { setData(await api.get(`procurement/supplier-invoices/${invoiceId}/history`)); }
    catch (e) { setErr(e.message || 'Could not load the history.'); }
  }, [invoiceId]);
  useEffect(() => { load(); }, [load]);

  // Reading the entry back from Sage rather than trusting that the post worked.
  // "Sage returned an id" is an assumption; this is the check.
  const verify = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api.post(`sage/invoice/${invoiceId}/verify`, {});
      if (!r.verified) setErr(r.issues?.join(' ') || 'Differences found.');
      await load();
    } catch (e) { setErr(e.message || 'Could not read it back from Sage.'); }
    finally { setBusy(false); }
  };

  const state = data && (STATE_LINE[data.state] || STATE_LINE.in_ops);

  return (
    <Modal
      title="Invoice history"
      onClose={onClose}
      footer={data?.invoice?.sage_invoice_id ? (
        <div className="flex items-center gap-2 w-full">
          <button onClick={verify} disabled={busy}
            className="px-3 py-1.5 text-[10px] font-bold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-50">
            {busy ? 'Checking…' : 'Verify against Sage'}
          </button>
          <span className="text-[8px] text-gray-400">
            Reads the entry back and compares the supplier, date, net, VAT and attachment.
          </span>
        </div>
      ) : null}
    >
        <p className="text-[9px] text-gray-400">
          {data?.invoice?.invoice_number || '—'} · {data?.invoice?.supplier || 'Unknown supplier'}
        </p>

        {state && (
          <div className={`mt-3 px-3 py-2 rounded border text-[10px] ${state.cls}`}>
            {state.text}
            {data.invoice.sage_invoice_number && <span className="ml-1 opacity-80">({data.invoice.sage_invoice_number})</span>}
          </div>
        )}
        {err && <div className="mt-2 px-3 py-2 rounded border border-red-200 bg-red-50 text-[10px] text-red-800">{err}</div>}

        <div className="pt-3 space-y-2.5">
          {(data?.events || []).map((e, i) => {
            const st = EVENT_STYLE[e.kind] || EVENT_STYLE.edit;
            return (
              <div key={i} className="flex gap-2">
                <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5 ${st.dot}`} />
                <div className="flex-1 min-w-0">
                  <div className={`text-[10px] font-semibold ${st.text}`}>{e.what}</div>
                  <div className="text-[8px] text-gray-400">
                    {e.at}{e.who ? ` · ${e.who}` : ''}
                  </div>
                  {e.detail && <div className="text-[9px] text-gray-600 mt-0.5 break-words">{e.detail}</div>}
                </div>
              </div>
            );
          })}
          {data && !data.events.length && (
            <div className="text-[10px] text-gray-400 text-center py-4">Nothing recorded yet.</div>
          )}
          {!data && !err && <div className="text-[10px] text-gray-400 text-center py-4">Loading…</div>}
        </div>
    </Modal>
  );
}

// ── Creating the supplier, from where the problem is hit ────────────────────
//
// Sage will not accept a purchase invoice without a contact_id, so a supplier
// that is not in Sage is a hard stop rather than a warning. The same preview and
// duplicate check as the Sage screen, put in front of the person who is actually
// blocked: sending them to another screen mid-task is how invoices end up
// abandoned half-processed.
function CreateSupplierInline({ supplierId, onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`sage/supplier/${supplierId}/create-preview`).then(setPreview)
      .catch(e => setErr(e.message || 'Could not check Sage.'));
  }, [supplierId]);

  const go = async (confirm) => {
    setBusy(true); setErr(null);
    try { await api.post(`sage/supplier/${supplierId}/create`, { confirm_not_existing: confirm }); onDone(); }
    catch (e) { setErr(e.message || 'Sage refused to create the contact.'); }
    finally { setBusy(false); }
  };
  const link = async (contactId) => {
    setBusy(true); setErr(null);
    try { await api.put(`sage/map-supplier/${supplierId}`, { sage_contact_id: contactId }); onDone(); }
    catch (e) { setErr(e.message || 'Could not link it.'); }
    finally { setBusy(false); }
  };

  const existing = preview?.possible_existing || [];

  return (
    <Modal title={`Add ${preview?.supplier?.name || 'supplier'} to Sage`} onClose={onClose}>
        {err &&<div className="text-[9px] px-2 py-1.5 rounded border border-red-200 bg-red-50 text-red-800 mb-2">{err}</div>}

        {/* The important half. Creating a near-duplicate supplier is the failure
            mode, and it is only avoidable if what might be duplicated is on
            screen next to the button that would do it. */}
        {existing.length > 0 && (
          <div className="px-2 py-1.5 rounded border border-amber-300 bg-amber-50 mb-2">
            <div className="text-[9px] font-semibold text-amber-900">Sage may already have this supplier</div>
            {existing.map(p => (
              <div key={p.id} className="text-[9px] text-amber-900 mt-1">
                <strong>{p.name}</strong> <span className="text-[8px]">({p.confidence})</span>
                <button onClick={() => link(p.id)} disabled={busy}
                  className="ml-1 px-1.5 py-0.5 text-[8px] border border-amber-400 rounded bg-white hover:bg-amber-100">
                  Use this one
                </button>
              </div>
            ))}
          </div>
        )}
        {preview && !existing.length && (
          <p className="text-[9px] text-gray-500 mb-2">
            Checked {preview.searched} Sage supplier contacts and found nothing like this one.
          </p>
        )}

        {preview?.payload?.contact && (
          <div className="text-[9px] text-gray-700 border border-gray-200 rounded p-2 mb-2 space-y-0.5">
            <div><span className="text-gray-400">Name</span> {preview.payload.contact.name}</div>
            {preview.payload.contact.tax_number && <div><span className="text-gray-400">VAT</span> {preview.payload.contact.tax_number}</div>}
            {preview.payload.contact.reference && <div><span className="text-gray-400">Ref</span> {preview.payload.contact.reference}</div>}
          </div>
        )}

        <button onClick={() => go(existing.length > 0)} disabled={busy || !preview || preview.problems?.length}
          className="w-full px-3 py-1.5 text-[10px] font-bold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-40">
          {busy ? 'Creating…' : existing.length ? 'Create anyway — different company' : 'Create in Sage'}
        </button>
    </Modal>
  );
}

// ── Post one invoice to Sage ────────────────────────────────────────────────
//
// Preview and push hit the same server-side resolver, so what is shown here is
// exactly what would be sent. Nothing posts without this button being pressed,
// and an invoice that already carries a Sage id can never be posted twice — that
// is enforced in the database, not by hiding the button.
function SagePanel({ invoice, onChanged, autoCheck = false, onAutoChecked, onEdit }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy]       = useState(false);
  const [result, setResult]   = useState(null);
  const [showJson, setShowJson] = useState(false);
  const [creatingSupplier, setCreatingSupplier] = useState(false);
  // Set only by a check that Sage answered 404 to, moments ago. It gates the
  // one action that can undo a posting, so it is never inferred from stored
  // state — a flag from last week is not evidence about now.
  const [missing, setMissing] = useState(false);

  // Declared above the "already in Sage" branch below, because the automatic
  // check needs it and hooks cannot live after a return.
  const runPreview = useCallback(async () => {
    setBusy(true); setResult(null);
    try { setPreview(await api.get(`sage/invoice/${invoice.id}/preview`)); }
    catch (e) { setResult({ ok: false, text: e.message || 'Could not build the preview.' }); }
    finally { setBusy(false); }
  }, [invoice.id]);

  // The invoice that was just saved with "Log & check Sage" checks itself, once.
  // This is the whole point of that button: reading an invoice off a PDF and
  // getting it ready for the accounts is one pass rather than a save here and a
  // hunt for the same invoice in the list underneath. It is deliberately not
  // done for every invoice on screen — the preview asks Sage whether the invoice
  // is already there, and that is one live request per row.
  const autoCheckDone = useRef(false);
  const panelRef = useRef(null);
  useEffect(() => {
    if (!autoCheck || autoCheckDone.current || invoice.sage_invoice_id) return;
    autoCheckDone.current = true;
    runPreview().finally(() => {
      // The list can be long enough that the invoice just saved is below the
      // fold, and a check nobody can see is a check nobody acts on.
      panelRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      onAutoChecked?.();
    });
  }, [autoCheck, invoice.sage_invoice_id, runPreview, onAutoChecked]);

  // The one blocker with a fix that belongs right here rather than on another
  // screen: Sage will not take the invoice at all without a contact_id.
  const needsSupplier = (preview?.problems || []).some(p => /not linked to a Sage contact/i.test(p));

  // Already in Sage — as far as ops knows.
  //
  // "Check in Sage" is what turns that into something known: it reads the entry
  // back, re-uploads the PDF if Sage does not have it, and says so plainly. If
  // Sage answers 404 the entry has been deleted there, and the invoice is
  // stranded — ops refuses to post it twice, Sage has never heard of it. That is
  // the one case where the link can be broken and the invoice posted again, and
  // it takes a second press so it is never something that just happens.
  // ── Posted, but ops cannot name the entry ──
  //
  // This is the state behind "it says Not in Sage, I press Check, it says Ready
  // to post — but we already sent it". The invoice went to Sage, Sage's reply
  // did not carry an id where the code looked for one, and ops recorded nothing
  // to link to. It must never read as unposted: that is one click from a second
  // copy in the ledger.
  if (!invoice.sage_invoice_id && invoice.sage_posted_unidentified) {
    const findIt = async () => {
      setBusy(true); setResult(null);
      try {
        const r = await api.post(`sage/invoice/${invoice.id}/find`, {});
        setResult({ ok: true, text: r.message || 'Linked.' });
        onChanged?.();
      } catch (e) {
        setResult({ ok: false, text: e.message || 'Could not find it in Sage.' });
      } finally { setBusy(false); }
    };
    return (
      <div className="mt-1.5 px-2 py-1 rounded text-[7px] bg-amber-50 border border-amber-200 text-amber-900">
        <div className="font-bold">⚠ Posted to Sage, entry not identified</div>
        <div className="mt-0.5">
          Sage accepted this invoice but did not return a reference for it, so ops cannot link to the entry.
          It will not be posted again. Find it to match them up.
        </div>
        <button onClick={findIt} disabled={busy}
          className="mt-1 px-2 py-0.5 rounded border border-amber-400 bg-white text-amber-900 font-semibold hover:bg-amber-100 disabled:opacity-40">
          {busy ? 'Looking…' : '⌕ Find it in Sage'}
        </button>
        {result && <div className={`mt-1 ${result.ok ? 'text-emerald-800' : 'text-red-700'}`}>{result.text}</div>}
      </div>
    );
  }

  if (invoice.sage_invoice_id) {
    const retryAttach = async () => {
      setBusy(true); setResult(null);
      try {
        await api.post(`sage/invoice/${invoice.id}/attach`, {});
        setResult({ ok: true, text: 'PDF attached in Sage.' });
        onChanged?.();
      } catch (e) { setResult({ ok: false, text: e.message || 'Could not attach the PDF.' }); }
      finally { setBusy(false); }
    };

    const checkInSage = async () => {
      setBusy(true); setResult(null); setMissing(false);
      try {
        const r = await api.post(`sage/invoice/${invoice.id}/verify`, {});
        const attached = r.attachment?.attached ? ' The PDF was missing in Sage and has been attached.' : '';
        // notes are what the check could not look at (a foreign invoice keyed
        // in Sage by hand has no known sterling value), so "all match" would
        // claim more than was checked.
        const notes = (r.notes || []).length ? ` ${r.notes.join(' ')}` : '';
        setResult(r.verified
          ? { ok: true, text: notes
              ? `Confirmed in Sage: supplier and date match.${notes}${attached}`
              : `Confirmed in Sage: supplier, date and amounts all match.${attached}` }
          : { ok: false, text: `${(r.issues || []).join(' ')}${notes}${attached}` });
        if (r.attachment?.attached) onChanged?.();
      } catch (e) {
        // can_repost comes from the server and means one thing only: Sage was
        // asked just now and answered 404. Read from the payload rather than
        // matched out of the message, so wording can change without quietly
        // disabling the one action that can undo a posting.
        setMissing(!!e.data?.can_repost);
        setResult({ ok: false, text: e.message || 'Could not check the invoice in Sage.' });
      } finally { setBusy(false); }
    };

    // Only reachable after a check has just been told 404 by Sage. The server
    // asks Sage again before it changes anything, so a stale screen cannot
    // unlink an entry that has since come back.
    const unlinkAndRepost = async () => {
      setBusy(true);
      try {
        const r = await api.post(`sage/invoice/${invoice.id}/unlink`, {});
        setResult({ ok: true, text: r.message || 'Link cleared. Post it again below.' });
        setMissing(false);
        onChanged?.();
      } catch (e) {
        setResult({ ok: false, text: e.message || 'Could not clear the link.' });
        setMissing(false);
      } finally { setBusy(false); }
    };

    return (
      <div className={`mt-1.5 px-2 py-1 rounded text-[7px] ${missing ? 'bg-red-50 border border-red-200 text-red-800' : 'bg-emerald-50 border border-emerald-200 text-emerald-800'}`}>
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="font-bold">{missing ? '✗ Not in Sage' : '✓ In Sage'}</span>
          {invoice.sage_invoice_number && <span>{invoice.sage_invoice_number}</span>}
          {invoice.sage_attachment_id ? (
            <span className="text-emerald-700">· PDF attached</span>
          ) : (
            <>
              <span className="text-amber-700">· no PDF attached</span>
              <button onClick={retryAttach} disabled={busy}
                className="px-1.5 py-0.5 rounded border border-amber-300 text-amber-800 bg-white hover:bg-amber-50 disabled:opacity-40 font-semibold">
                {busy ? 'Attaching…' : 'Attach PDF'}
              </button>
            </>
          )}
          <button onClick={checkInSage} disabled={busy}
            className="px-1.5 py-0.5 rounded border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 font-semibold">
            {busy ? 'Checking…' : 'Check in Sage'}
          </button>
          {missing && (
            <button onClick={unlinkAndRepost} disabled={busy}
              className="px-1.5 py-0.5 rounded border border-red-300 text-red-800 bg-white hover:bg-red-50 disabled:opacity-40 font-semibold">
              Clear the link so it can be posted again
            </button>
          )}
        </div>
        {result && (
          <div className={`mt-1 ${result.ok ? 'text-emerald-800' : 'text-red-700'}`}>{result.text}</div>
        )}
      </div>
    );
  }

  const notDuplicate = async () => {
    const note = window.prompt(
      `Why is ${invoice.invoice_number || 'this invoice'} not a duplicate?\n\n`
      + 'Check the amount and date against what is already in Sage first. This is recorded against the invoice.');
    if (note === null) return;
    setBusy(true); setResult(null);
    try {
      await api.post(`sage/invoice/${invoice.id}/not-duplicate`, { note });
      setPreview(await api.get(`sage/invoice/${invoice.id}/preview`));
    } catch (e) { setResult({ ok: false, text: e.message || 'Could not record that.' }); }
    finally { setBusy(false); }
  };

  const push = async () => {
    // No prompt on a clean invoice.
    //
    // It used to ask every time, and an "are you sure, this writes to the
    // accounts" on a perfectly ordinary invoice is alarming rather than
    // careful: it implies the app is unsure, when in fact it has just checked
    // the supplier, the ledger code, the VAT, the evidence and the duplicate
    // history and found nothing wrong. Somebody posting forty invoices learns
    // to dismiss it without reading, which is exactly the habit you do not want
    // them to have on the one that matters.
    //
    // Nothing is lost by dropping it. Getting here already takes Check then
    // Post, posting is off entirely unless it has been switched on, a blocked
    // invoice cannot be posted at all, and an invoice already in Sage can never
    // post twice — that last one is enforced in the database, not by a dialog.
    //
    // So the prompt is kept only where a judgement is genuinely being made: an
    // unanswered warning, or an overridden duplicate — which arrives as a
    // warning too. Then it names what it is asking about rather than asking in
    // general, which is the only version anybody reads.
    const reasons = preview?.warnings || [];
    if (reasons.length && !confirm(
      `Post ${invoice.invoice_number || 'this invoice'} to Sage?\n\n${reasons.map(w => `• ${w}`).join('\n')}`
    )) return;
    setBusy(true); setResult(null);
    try {
      const r = await api.post(`sage/invoice/${invoice.id}/push`, {});
      // A foreign invoice comes back with Sage's own sterling value checked
      // against ours. A disagreement is a wrong ledger entry, so it is shown
      // as one rather than tucked behind "Posted".
      const fxWrong = r.fx_check && r.fx_check.ok === false;
      setResult({
        ok: !fxWrong,
        text: `Posted to Sage.${r.attachment?.ok ? ' PDF attached.' : r.attachment ? ` PDF not attached: ${r.attachment.error}` : ''}`
          + (r.fx_check?.message ? ` ${r.fx_check.message}` : '')
          + (r.fx_check?.ok ? ` Sage booked it at £${Number(r.fx_check.sage_gbp).toFixed(2)}, as expected.` : ''),
      });
      onChanged?.();
    } catch (e) {
      setResult({ ok: false, text: e.message || 'Sage rejected the invoice.' });
    } finally { setBusy(false); }
  };

  // "Everything goes to Sage until specifically excluded with a reason." This is
  // that exclusion: a reason is required, it is recorded against the invoice with
  // who decided it, and it takes the invoice off the outstanding list without
  // pretending it was posted.
  const exclude = async () => {
    const reason = window.prompt(
      `Why is ${invoice.invoice_number || 'this invoice'} not going to Sage?\n\n`
      + 'Everything goes to the ledger unless there is a reason not to. This is recorded '
      + 'against the invoice with your name, and takes it off the outstanding list.');
    if (reason === null) return;
    if (!reason.trim()) { setResult({ ok: false, text: 'A reason is needed to exclude an invoice.' }); return; }
    setBusy(true); setResult(null);
    try {
      await api.put(`procurement/supplier-invoices/${invoice.id}/sage-exclusion`, { reason: reason.trim() });
      setResult({ ok: true, text: 'Excluded from Sage, with the reason recorded.' });
      onChanged?.();
    } catch (e) { setResult({ ok: false, text: e.message || 'Could not record that.' }); }
    finally { setBusy(false); }
  };

  const unexclude = async () => {
    setBusy(true); setResult(null);
    try {
      await api.put(`procurement/supplier-invoices/${invoice.id}/sage-exclusion`, { reason: '' });
      setResult({ ok: true, text: 'Back on the list for Sage.' });
      onChanged?.();
    } catch (e) { setResult({ ok: false, text: e.message || 'Could not record that.' }); }
    finally { setBusy(false); }
  };

  // ── "IS IT ALREADY IN SAGE?" ────────────────────────────────────────────────
  //
  // The migration case. Everything before ops went live is already in Sage, keyed
  // by hand, so the answer for a lot of these invoices is "it is there, link it"
  // rather than "post it". Posting would put it in the ledger twice.
  //
  // The search deliberately does not assume where the number lives: Sage entries
  // have been seen with our PO number in Reference and the supplier's number in
  // Supplier Reference, and the other way round, so both of ours are searched
  // against both of theirs, plus the total on its own.
  const [existing, setExisting] = useState(null);
  const [searchingSage, setSearchingSage] = useState(false);
  const findExisting = async () => {
    setSearchingSage(true); setResult(null);
    try {
      setExisting(await api.get(`sage/invoice/${invoice.id}/find-existing`));
    } catch (e) {
      setResult({ ok: false, text: e.message || 'Could not search Sage.' });
    } finally { setSearchingSage(false); }
  };
  // The search reads a local copy of Sage's purchase invoices, so when that copy
  // cannot answer for this invoice the fix is to top it up — from here, rather than
  // by waiting for the hourly sync or hunting for the Sage screen.
  const refreshSageCache = async () => {
    setSearchingSage(true); setResult(null);
    try {
      // If the first full fill has not finished, this starts the job rather than
      // pulling one more batch and leaving somebody to press it again. The job then
      // runs itself to the end on a three-minute tick, so the honest thing to say is
      // how far along it is and roughly how long is left.
      const done = existing?.cache?.progress?.state === 'complete';
      const r = await api.post(done ? 'sage/cache/sync' : 'sage/cache/backfill', done ? { mode: 'auto' } : {});
      const c = r.cache || {};
      const p = c.progress || {};
      const left = p.pages_left != null && p.per_run
        ? Math.max(1, Math.ceil(p.pages_left / p.per_run) * (p.tick_minutes || 3))
        : null;
      setResult({
        ok: true,
        text: c.ready
          ? `Our copy of Sage is up to date — ${(c.entries || 0).toLocaleString('en-GB')} entries.`
          : `Filling our copy of Sage: ${(p.held || 0).toLocaleString('en-GB')}`
            + `${p.expected != null ? ` of ${p.expected.toLocaleString('en-GB')}` : ''} entries`
            + `${p.percent != null ? ` (${p.percent}%)` : ''}. It carries on by itself`
            + `${left ? `, about ${left} minute${left === 1 ? '' : 's'} to go` : ''} — the Sage screen shows progress.`,
      });
      setExisting(await api.get(`sage/invoice/${invoice.id}/find-existing`));
    } catch (e) {
      setResult({ ok: false, text: e.message || 'Could not refresh the Sage copy.' });
    } finally { setSearchingSage(false); }
  };
  const linkExisting = async (candidate) => {
    if (!window.confirm(
      `Link ${invoice.invoice_number || 'this invoice'} to the entry already in Sage?\n\n`
      + `${candidate.contact_name || 'Sage entry'}${candidate.reference ? ` · ${candidate.reference}` : ''}`
      + `${candidate.total_amount != null ? ` · £${Number(candidate.total_amount).toFixed(2)}` : ''}\n\n`
      + 'Nothing is posted. This records that the ledger already has it, so it stops being outstanding.')) return;
    setSearchingSage(true); setResult(null);
    try {
      const r = await api.post(`sage/invoice/${invoice.id}/link-existing`, { sage_invoice_id: candidate.id });
      setResult({ ok: true, text: `Linked to the entry already in Sage${r.contact_name ? ` under ${r.contact_name}` : ''}.` });
      setExisting(null);
      onChanged?.();
    } catch (e) {
      // A total mismatch comes back as a refusal with a reason; the person doing
      // the migration may know why, so it can be confirmed rather than blocked.
      if (/totals do not match/i.test(e.message || '')
          && window.confirm(`${e.message}\n\nLink them anyway?`)) {
        try {
          const r = await api.post(`sage/invoice/${invoice.id}/link-existing`,
            { sage_invoice_id: candidate.id, confirm_mismatch: true });
          setResult({ ok: true, text: `Linked${r.mismatch ? ` — noted: ${r.mismatch}` : ''}.` });
          setExisting(null);
          onChanged?.();
        } catch (e2) { setResult({ ok: false, text: e2.message || 'Could not link that.' }); }
      } else {
        setResult({ ok: false, text: e.message || 'Could not link that.' });
      }
    } finally { setSearchingSage(false); }
  };


  // The stored verdict, computed on every save and swept hourly (see
  // sweepSagePostReadiness). A live preview supersedes it when there is one, because it
  // is the fresher answer to the same question.
  const readiness = (() => {
    if (preview) {
      return {
        known: true, ready: !(preview.problems || []).length,
        blockers: preview.problems || [], checkedAt: null,
      };
    }
    if (invoice.sage_ready == null) return { known: false, ready: false, blockers: [] };
    let blockers = [];
    try { blockers = JSON.parse(invoice.sage_blockers || '[]'); } catch { blockers = []; }
    return {
      known: true, ready: invoice.sage_ready === 1 || invoice.sage_ready === true,
      blockers, checkedAt: invoice.sage_ready_checked_at || null,
    };
  })();

  // Blocked by whichever answer we have: the live preview when there is one, the swept
  // verdict otherwise. Post is offered off the stored verdict, so it has to be gated by
  // the stored blockers too, or the button would be live on an invoice known to fail.
  const blocked = readiness.known ? !readiness.ready : (preview?.problems || []).length > 0;
  // Which problems the invoice form can actually fix, as opposed to the supplier
  // link, which is fixed in Sage and has its own action below. Matched on the
  // text because that is what the preview returns; the fallback is to treat a
  // problem as fixable, since offering Edit for something it will not fix is a
  // wasted click, while hiding it on an unrecognised message is a dead end.
  const problems = preview?.problems || [];
  // Only what editing the invoice actually fixes. A missing exchange rate is
  // set in the rate box above, and the EU options and a foreign invoice with
  // VAT are matters for Sage, so "Edit the invoice" against those sent people
  // to a form with nothing on it to change.
  const NOT_FIXED_BY_EDITING = /not linked to a Sage contact|no exchange rate|EU Goods\/Services option|charges VAT\. A foreign-currency invoice/i;
  const fixableByEditing = problems.filter(p => !NOT_FIXED_BY_EDITING.test(p));
  const missingLedgerOnly = fixableByEditing.length === 1
    && /No Sage ledger account/i.test(fixableByEditing[0]);
  // Posting switched off is not a problem with the invoice, so it is not one of
  // its problems. It is still a reason the button will not work, and saying so
  // beats being refused after pressing it.
  const postingOff = preview && preview.posting_enabled === false;
  // A preview that comes back saying the invoice is already in Sage must not
  // still offer Post. The row this component was given can be a few seconds old.
  const alreadyThere = preview && (preview.already_in_sage || preview.posted_unidentified);

  return (
    <div ref={panelRef} className="mt-1.5 border-t border-gray-100 pt-1.5">
      {/* Two buttons with no statement of where things stand read as a queue
          somebody else works through. They are not: nothing leaves ops until
          one of them is pressed. */}
      {/* An exclusion is a finished state, so it is said first and the rest of the
          panel stops nagging. */}
      {invoice.sage_excluded_reason ? (
        <div className="mb-1 px-2 py-1 rounded bg-gray-50 border border-gray-200 text-[7px] text-gray-700">
          <span className="font-bold">Deliberately not going to Sage.</span> {invoice.sage_excluded_reason}
          <button onClick={unexclude} disabled={busy}
            className="ml-1.5 underline text-navy font-semibold disabled:opacity-40">
            Put it back on the list
          </button>
        </div>
      ) : !preview && !existing && (
        <div className="text-[7px] mb-1 leading-relaxed">
          <span className="font-bold text-amber-800">Not in Sage.</span>{' '}
          <span className="text-amber-800">
            Every invoice goes to the ledger unless there is a reason it should not, and it is not finished until
            Sage has accepted it.
          </span>
          {/* WOULD IT POST? ANSWERED, NOT ASKED.
              There used to be a "Will it post?" button here. Nothing about the answer
              needed a person — it is the same builder the posting uses, over rows
              already in the database — so it is computed on every save and swept
              hourly, and the state is simply shown. Hourly as well as on save because
              readiness changes when the invoice does not: a supplier gets its Sage
              contact, a ledger default is set, a VAT setting is switched on. */}
          {readiness.known ? (
            <div className={`mt-1 px-2 py-1 rounded border ${readiness.ready
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : 'bg-amber-50 border-amber-300 text-amber-900'}`}>
              {readiness.ready ? (
                <span><span className="font-bold">Ready to post.</span> Everything Sage needs is here.</span>
              ) : (
                <>
                  <span className="font-bold">
                    {readiness.blockers.length} thing{readiness.blockers.length === 1 ? '' : 's'} stop{readiness.blockers.length === 1 ? 's' : ''} this posting:
                  </span>
                  <ul className="mt-0.5 ml-2 list-disc list-inside space-y-0.5">
                    {readiness.blockers.map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                </>
              )}
              {readiness.checkedAt && (
                <div className="text-[6px] opacity-70 mt-0.5">
                  Checked {readiness.checkedAt} UTC, and again on every save and every hour.
                </div>
              )}
            </div>
          ) : (
            <div className="mt-1 text-gray-500">
              Not checked yet — the hourly sweep will pick it up, or press Check now.
            </div>
          )}
          <div className="text-gray-500 mt-0.5">
            <strong>Search the ledger</strong> looks for an entry already there from before ops — link that instead
            of posting.
          </div>
        </div>
      )}

      {/* The last attempt that failed, from the invoice rather than from this
          component's own state — so it survives a reload and is still there
          when somebody comes back to the invoice later. A failed post that
          leaves no trace on the screen is how "I pressed it and nothing
          happened" becomes an invoice nobody chases. */}
      {invoice.sage_push_error && !result && (
        <div className="mb-1 px-2 py-1 rounded bg-red-50 border border-red-200 text-[7px] text-red-800">
          <span className="font-semibold">The last attempt to post this was refused by Sage:</span>{' '}
          {invoice.sage_push_error}
        </div>
      )}
      <div className="flex items-center gap-1 flex-wrap">
        {/* TWO CHECKS, TWO DIFFERENT QUESTIONS — and the old labels ("Check for
            Sage" and "Already in Sage?") did not say which was which.
              • this one: WILL IT POST? Validates the payload Sage would receive —
                contact, nominal code, VAT, evidence — and checks the supplier's own
                Sage history for a duplicate about to be created.
              • the other: IS IT ALREADY THERE? Searches the whole ledger by invoice
                number and amount for an entry that predates ops, which is the
                migration case and needs linking rather than posting. */}
        {/* Not "will it post?" — that is answered above without asking. This runs the
            full preview: the exact payload, plus the live duplicate check against the
            supplier's own Sage history, which is the one part that does need a request.
            Posting runs it again anyway, so this is for looking before leaping rather
            than a gate to get through. */}
        <button onClick={runPreview} disabled={busy}
          className="px-2 py-0.5 text-[7px] border border-gray-200 text-gray-600 rounded hover:bg-gray-50 disabled:opacity-40"
          title="Show exactly what would be sent to Sage, and check against this supplier's existing entries for a duplicate">
          {busy && !preview ? 'Checking…' : readiness.known ? '⚙ Check now' : '⚙ Check it'}
        </button>
        {/* Post is offered off the stored verdict as well as off a preview: making
            somebody press Check first to reveal the button was the same "press this to
            find out" it replaced. The push re-runs every check server-side regardless,
            so nothing is trusted that has not just been re-verified. */}
        {(preview || readiness.ready) && (
          <button onClick={push} disabled={busy || blocked || postingOff || alreadyThere}
            className="px-2 py-0.5 text-[7px] font-bold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-40"
            title={alreadyThere ? 'This invoice is already in Sage'
                 : postingOff ? 'Posting to Sage is switched off in Sage settings'
                 : blocked ? 'Fix the problems listed below first' : 'Post this invoice to Sage'}>
            {busy ? 'Posting…' : '→ Post to Sage'}
          </button>
        )}
        {preview && (
          <button onClick={() => setShowJson(v => !v)}
            className="px-2 py-0.5 text-[7px] text-gray-400 hover:text-gray-600 underline">
            {showJson ? 'hide payload' : 'show payload'}
          </button>
        )}
        {/* The migration question, asked of Sage rather than assumed. */}
        {!alreadyThere && (
          <button onClick={findExisting} disabled={searchingSage}
            className="px-2 py-0.5 text-[7px] border border-gray-200 text-gray-600 rounded hover:bg-gray-50 disabled:opacity-40"
            title="Search the whole ledger for an entry with this invoice number or amount — for invoices keyed into Sage before ops, under any supplier">
            {searchingSage ? 'Searching the ledger…' : '⌕ Search the ledger'}
          </button>
        )}
        {/* The only sanctioned way out of posting. Deliberately last, deliberately
            plain, and it asks for a reason. */}
        {!invoice.sage_excluded_reason && !alreadyThere && (
          <button onClick={exclude} disabled={busy}
            className="ml-auto px-2 py-0.5 text-[7px] text-gray-400 hover:text-gray-700 underline disabled:opacity-40"
            title="Record a reason this invoice is not going to Sage">
            not going to Sage…
          </button>
        )}
      </div>

      {/* What Sage already holds. Shown with the field the number was found in,
          because "matched on Supplier Reference under a different contact name" is
          the fact that makes an unexpected match believable — and the reason a
          contact-scoped duplicate check missed it. */}
      {existing && <ExistingInSage found={existing} busy={searchingSage}
        onLink={linkExisting} onClose={() => setExisting(null)} onRefreshCache={refreshSageCache} />}

      {/* The supplier's Sage contact, defaulted rather than asked for. Said out
          loud because the invoice has just acquired an accounting identity, and
          somebody should be able to see it and disagree. */}
      {preview?.contact_auto_linked && (
        <div className="mt-1 px-2 py-1 rounded bg-emerald-50 border border-emerald-200 text-[7px] text-emerald-800">
          Supplier matched to the Sage contact <strong>{preview.contact_auto_linked.name}</strong>
          {preview.contact_auto_linked.reference ? ` (${preview.contact_auto_linked.reference})` : ''} automatically —
          the names are the same. Change it on the Sage screen if that is wrong.
        </div>
      )}
      {/* A resemblance, not a certainty: offered, never applied. Posting against
          the wrong account puts money against the wrong name in the accounts. */}
      {preview?.contact_suggestion && (
        <div className="mt-1 px-2 py-1.5 rounded bg-amber-50 border border-amber-200">
          <div className="text-[7px] text-amber-900">
            This supplier might be <strong>{preview.contact_suggestion.name}</strong> in Sage.{' '}
            {preview.contact_suggestion.reason} Not linked automatically because it is not an exact match.
          </div>
          <button
            onClick={async () => {
              setBusy(true);
              try {
                await api.put(`sage/map-supplier/${invoice.supplier_id}`, { sage_contact_id: preview.contact_suggestion.id });
                await runPreview();
                onChanged?.();
              } catch (e) { setResult({ ok: false, text: e.message || 'Could not link that.' }); }
              finally { setBusy(false); }
            }}
            disabled={busy || !invoice.supplier_id}
            className="mt-1 px-2 py-0.5 text-[7px] font-semibold border border-amber-400 text-amber-900 rounded bg-white hover:bg-amber-100 disabled:opacity-40">
            Yes, that is them
          </button>
        </div>
      )}

      {preview?.vat && (
        <div className="mt-1 text-[7px] text-gray-500">
          VAT: {preview.vat.treatment || 'not established'}
          {preview.vat.effective_percent != null ? ` · ${preview.vat.effective_percent}%` : ''}
          {preview.vat.reclaimable > 0 ? ` · £${preview.vat.reclaimable.toFixed(2)} reclaimable` : ''}
          {preview.ledger_account_source ? ` · code from the ${preview.ledger_account_source}` : ''}
        </div>
      )}

      {preview?.fx && (
        <FxRateBox invoice={invoice} fx={preview.fx} gross={preview.totals?.gross}
          onSaved={runPreview} />
      )}

      {(preview?.problems || []).map((p, i) => (
        <div key={`p${i}`} className="mt-1 px-2 py-1 rounded bg-red-50 border border-red-200 text-[7px] text-red-700">✕ {p}</div>
      ))}

      {/* The way out of a blocked invoice.
          Nearly every problem the checks raise — no nominal code, no invoice
          number, no date, VAT not established, an amount that does not add up —
          is fixed by editing the invoice. The list above said what was wrong and
          then left the reader looking at two buttons that will not work, with
          Edit a grey word in the corner of the row above. Reading "No Sage
          ledger account has been chosen" as an error in the app rather than as a
          field to fill in is the natural conclusion.
          So the action is offered here, at the problem, and it says what it is
          for. The supplier case keeps its own button below because that one is
          fixed in Sage rather than on the invoice. */}
      {blocked && !alreadyThere && !postingOff && fixableByEditing.length > 0 && (
        <div className="mt-1 px-2 py-1.5 rounded bg-red-50 border border-red-300">
          <div className="text-[7px] text-red-900 font-bold">
            This invoice is not in Sage and cannot go until {fixableByEditing.length > 1 ? 'these are' : 'this is'} fixed on it.
          </div>
          <button onClick={() => onEdit?.(invoice)} disabled={!onEdit}
            className="mt-1 px-2 py-0.5 text-[7px] font-bold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-40"
            title="Open this invoice and fill in what Sage needs">
            ✎ Edit the invoice{missingLedgerOnly ? ' and add the nominal code' : ''}
          </button>
          <span className="ml-1.5 text-[7px] text-red-800">then check it again</span>
        </div>
      )}

      {/* Sage will not take a purchase invoice without a contact_id, so a
          supplier that is not in Sage is a hard stop. Sending somebody off to
          another screen to fix it mid-task is how invoices get abandoned
          half-processed, so it is offered here, where the problem is hit. The
          preview and its duplicate check are exactly the same ones. */}
      {needsSupplier && (
        <div className="mt-1 px-2 py-1.5 rounded bg-amber-50 border border-amber-200">
          <div className="text-[7px] text-amber-900 font-semibold">
            Sage needs the supplier before it will take this invoice.
          </div>
          <button onClick={() => setCreatingSupplier(true)} disabled={busy || !invoice.supplier_id}
            className="mt-1 px-2 py-0.5 text-[7px] font-semibold border border-amber-400 text-amber-900 rounded bg-white hover:bg-amber-100 disabled:opacity-40"
            title={invoice.supplier_id ? 'Create this supplier in Sage' : 'Link the invoice to a supplier record first'}>
            {invoice.supplier_id ? 'Create the supplier in Sage' : 'Pick a supplier on the invoice first'}
          </button>
        </div>
      )}
      {creatingSupplier && (
        <CreateSupplierInline supplierId={invoice.supplier_id}
          onClose={() => setCreatingSupplier(false)}
          onDone={async () => { setCreatingSupplier(false); await runPreview(); onChanged?.(); }} />
      )}

      {/* The way past a duplicate judgement. Offered only when a possible
          duplicate is the ONLY thing in the way — never against an invoice
          already in Sage or one on the AutoEntry route, because those are facts
          rather than judgements. */}
      {preview?.duplicate?.overridable && (
        <button onClick={notDuplicate} disabled={busy}
          className="mt-1 px-2 py-0.5 text-[7px] font-semibold border border-amber-400 text-amber-800 rounded bg-white hover:bg-amber-50 disabled:opacity-40">
          This is not a duplicate
        </button>
      )}
      {(preview?.warnings || []).map((w, i) => (
        <div key={`w${i}`} className="mt-1 px-2 py-1 rounded bg-amber-50 border border-amber-200 text-[7px] text-amber-800">⚠ {w}</div>
      ))}
      {postingOff && (
        <div className="mt-1 px-2 py-1 rounded bg-gray-50 border border-gray-200 text-[7px] text-gray-600">
          Posting to Sage is switched off. Turn on sage_posting_enabled in Sage settings to post this.
        </div>
      )}
      {alreadyThere && (
        <div className="mt-1 px-2 py-1 rounded bg-amber-50 border border-amber-200 text-[7px] text-amber-900">
          This invoice is already in Sage. Reload the screen — what is shown here is out of date.
        </div>
      )}
      {preview && !blocked && !postingOff && !alreadyThere && !(preview.warnings || []).length && (
        <div className="mt-1 px-2 py-1 rounded bg-emerald-50 border border-emerald-200 text-[7px] text-emerald-800">
          ✓ Ready to post. {preview.has_pdf ? 'The PDF will be attached.' : 'No PDF is stored for this one.'}
        </div>
      )}

      {showJson && preview && (
        <pre className="mt-1 bg-gray-50 border border-gray-200 rounded p-1.5 text-[7px] overflow-x-auto max-h-40">
{JSON.stringify(preview.payload, null, 2)}
        </pre>
      )}

      {result && (
        <div className={`mt-1 px-2 py-1 rounded text-[7px] ${result.ok
          ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
          : 'bg-red-50 border border-red-200 text-red-700'}`}>
          {result.text}
          {/* A refused post caused by the connection rather than by the invoice.
              The fix is on another screen, so the link goes with the message. */}
          {!result.ok && /disconnected|reconnect/i.test(result.text || '') && (
            <a href="#sage" className="ml-1 font-bold underline">Reconnect Sage →</a>
          )}
        </div>
      )}
    </div>
  );
}

// ── The exchange rate a foreign invoice posts at ─────────────────────────────
//
// The Bank of England's daily spot rate for the invoice date unless somebody
// sets another, as units of the invoice currency per £1, the way the Bank
// publishes it. The sterling total is shown beside the rate because that is the
// figure that goes in the ledger, and it is what catches a rate typed the wrong
// way round: 0.84 where 1.19 was meant puts the total out by forty per cent.
function FxRateBox({ invoice, fx, gross, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const ccy = fx.currency;
  const sym = currencySymbol(ccy) || `${ccy} `;
  const longDate = (d) => (d ? new Date(`${d}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }) : '');

  const save = async (rate) => {
    setSaving(true); setError(null);
    try {
      await api.post(`sage/invoice/${invoice.id}/fx-rate`, { rate });
      setEditing(false);
      await onSaved?.();
    } catch (e) { setError(e.message || 'Could not save the rate.'); }
    finally { setSaving(false); }
  };

  const typed = Number(value);
  const typedOk = value !== '' && Number.isFinite(typed) && typed > 0;
  const reference = fx.boe_rate || fx.rate;
  // Looks like £ per unit typed into a box that wants units per £: the inverse
  // of it lands near the reference rate.
  const looksInverted = typedOk && reference && Math.abs(typed - reference) / reference > 0.1
    && Math.abs(1 / typed - reference) / reference < 0.1;
  const farOff = typedOk && reference && !looksInverted && Math.abs(typed - reference) / reference > 0.1;

  const source = fx.source === 'manual'
    ? `set by hand${fx.set_by_name ? ` by ${fx.set_by_name}` : ''}${fx.boe_rate ? `; the Bank of England rate is ${fx.boe_rate}` : ''}`
    : fx.source === 'boe'
      ? `Bank of England rate for ${longDate(fx.rate_date)}${fx.rate_date && fx.rate_date !== invoice.invoice_date ? ', the last published on or before the invoice date' : ''}`
      : null;

  return (
    <div className="mt-1 px-2 py-1.5 rounded bg-blue-50 border border-blue-200 text-[7px] text-blue-900">
      <div className="font-bold">This invoice is in {ccy}.</div>
      {fx.rate ? (
        <div className="mt-0.5">
          Goes to Sage in pounds: <span className="font-bold">{formatMoney(fx.gbp_gross)}</span>
          {gross != null ? `, converted from ${formatMoney(gross, 2, ccy)}` : ''} at <span className="font-bold">{sym}{fx.rate} = £1</span>
          {source ? ` (${source})` : ''}.
          {' '}The {ccy} amount and the rate go in the Sage notes.
        </div>
      ) : fx.pending ? (
        <div className="mt-0.5">The rate is looked up when the invoice is checked.</div>
      ) : (
        <div className="mt-0.5 text-red-700">{fx.error || 'There is no rate yet.'}</div>
      )}

      {!editing && (
        <div className="mt-1 flex gap-1.5">
          <button onClick={() => { setValue(String(fx.rate || fx.boe_rate || '')); setEditing(true); setError(null); }}
            disabled={saving}
            className="px-2 py-0.5 font-semibold border border-blue-300 text-blue-800 rounded bg-white hover:bg-blue-100 disabled:opacity-40">
            {fx.rate ? 'Change rate' : 'Enter a rate'}
          </button>
          {fx.source === 'manual' && (
            <button onClick={() => save(null)} disabled={saving}
              className="px-2 py-0.5 font-semibold border border-blue-300 text-blue-800 rounded bg-white hover:bg-blue-100 disabled:opacity-40">
              Use the Bank of England rate
            </button>
          )}
        </div>
      )}

      {editing && (
        <div className="mt-1">
          <label className="flex items-center gap-1">
            <span>{ccy} per £1</span>
            <input type="number" step="0.0001" min="0" value={value} autoFocus
              onChange={e => setValue(e.target.value)}
              className="w-20 px-1 py-0.5 border border-gray-300 rounded bg-white text-[8px] text-navy" />
          </label>
          {typedOk && (
            <div className="mt-0.5">
              {sym}1 = £{(1 / typed).toFixed(4)}.
              {gross != null ? ` ${formatMoney(gross, 2, ccy)} would go to Sage as ${formatMoney(Number(gross) / typed)}.` : ''}
            </div>
          )}
          {looksInverted && (
            <div className="mt-0.5 px-1.5 py-1 rounded bg-amber-50 border border-amber-200 text-amber-900">
              That looks like pounds per {ccy}. This box takes {ccy} per £1, which would be {(1 / typed).toFixed(4)}.
              <button onClick={() => setValue((1 / typed).toFixed(4))}
                className="ml-1 px-1.5 py-0.5 font-semibold border border-amber-400 rounded bg-white hover:bg-amber-100">
                Use {(1 / typed).toFixed(4)}
              </button>
            </div>
          )}
          {farOff && (
            <div className="mt-0.5 px-1.5 py-1 rounded bg-amber-50 border border-amber-200 text-amber-900">
              That is more than 10% away from the Bank of England rate of {reference}. Check it before saving.
            </div>
          )}
          {error && <div className="mt-0.5 text-red-700">{error}</div>}
          <div className="mt-1 flex gap-1.5">
            <button onClick={() => save(typed)} disabled={saving || !typedOk}
              className="px-2 py-0.5 font-bold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-40">
              {saving ? 'Saving…' : 'Save rate'}
            </button>
            <button onClick={() => { setEditing(false); setError(null); }} disabled={saving}
              className="px-2 py-0.5 font-semibold border border-gray-300 text-gray-700 rounded bg-white hover:bg-gray-50 disabled:opacity-40">
              {fx.rate ? `Keep ${fx.rate}` : 'Cancel'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── "Already in Sage?" — our record beside theirs ────────────────────────────
//
// The first version of this was three lines of small grey text per candidate and a
// one-word verdict, which is not enough to say yes to. Confirming that a ledger
// entry IS this invoice means comparing the things that identify it — the invoice
// number, the amount, the date, the supplier — and the useful presentation is
// therefore both records side by side with the agreements and the differences
// marked.
//
// It matters that the differences are shown rather than hidden. The interesting
// cases all involve something not matching: the supplier name is different because
// EMBS invoice on Simplicity's behalf, or the reference is our PO number because of
// how somebody keyed it. Those are the moments where a person's knowledge decides,
// and they cannot decide on a verdict that hides its reasons.
function ExistingInSage({ found, busy, onLink, onClose, onRefreshCache }) {
  const ours = found.ours || {};
  const money = (v) => (v == null ? '—' : `£${Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
  const CONF = {
    certain:        { label: 'Invoice number and amount both match', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
    invoice_number: { label: 'Invoice number matches exactly, amount differs', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
    // A partial number hit is only ever shown when something else agrees, and the
    // label says it is partial — "matches" for a substring is how a £14,040 invoice
    // for another supplier got offered against a £974 one.
    partial_number: { label: 'Part of the invoice number matches', cls: 'bg-amber-50 text-amber-800 border-amber-200' },
    likely:         { label: 'Amount and PO number match', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
    amount:         { label: 'Amount matches only', cls: 'bg-gray-100 text-gray-700 border-gray-300' },
    po_number:      { label: 'PO number matches only', cls: 'bg-gray-100 text-gray-700 border-gray-300' },
    // Nothing about the document matched. It is on the list because Sage holds it
    // against this supplier at about the right time, which is a prompt to look and
    // must not be dressed up as anything more.
    supplier_date:  { label: 'Nothing matched — same supplier, around this date', cls: 'bg-gray-100 text-gray-600 border-gray-300' },
  };

  if (!found.candidates?.length) {
    // Absence only means something when the search was in a position to see the
    // whole ledger. "Not in Sage" off an incomplete or stale copy is a wrong answer
    // that reads like a considered one — and the next action after it is to post a
    // duplicate. So the two answers are kept apart, and the second one says what is
    // missing and offers the fix.
    const sure = found.exhaustive;
    const cache = found.cache || {};
    return (
      <div className={`mt-1.5 rounded border px-2.5 py-2 ${sure ? 'border-gray-300 bg-gray-50' : 'border-amber-300 bg-amber-50'}`}>
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className={`text-[9px] font-bold ${sure ? 'text-gray-800' : 'text-amber-900'}`}>
              {sure ? 'Not in Sage' : 'Cannot tell yet'}
            </div>
            <div className={`text-[8px] mt-0.5 leading-relaxed ${sure ? 'text-gray-600' : 'text-amber-800'}`}>
              Searched {(found.scanned || 0).toLocaleString('en-GB')} Sage entr{found.scanned === 1 ? 'y' : 'ies'} for{' '}
              {(found.searched?.references || []).map(r => (
                <strong key={r.raw || r} className="font-mono">{r.raw || r}</strong>
              )).reduce((acc, el, i) => i ? [...acc, ' and ', el] : [el], [])}
              {' '}and {money(found.searched?.amount)}, on both the reference and the supplier reference
              {found.searched?.by_contact ? ", including everything under this supplier's Sage contact" : ''}.
              {sure
                ? ' Nothing matched, so this one needs posting.'
                : ` Our copy of the ledger cannot answer for this invoice yet${cache.reason ? `: ${cache.reason}` : ''}. That is not proof it is absent, so check in Sage before posting, or link it here if you find it.`}
            </div>
            {/* What stood behind the answer, either way, and where the fill has got
                to — because "not finished" on its own is indistinguishable from "not
                working", and that is exactly how this looked when it was broken. */}
            <div className={`text-[7px] mt-1 ${sure ? 'text-gray-400' : 'text-amber-700'}`}>
              {cache.oldest_date && cache.newest_date
                ? `Covers ${cache.oldest_date} to ${cache.newest_date}. ` : ''}
              {cache.last_sync_at
                ? `Last synced ${cache.age_hours != null && cache.age_hours < 1 ? 'under an hour' : `${cache.age_hours}h`} ago. `
                : 'Never synced. '}
              {cache.progress && cache.progress.state !== 'complete' && (
                cache.progress.state === 'blocked'
                  ? 'The Sage copy cannot be filled until the database is brought up to date — see the Sage screen.'
                  : cache.progress.state === 'failed'
                  ? `The full sync stopped after ${cache.progress.failures} refusals from Sage — see the Sage screen.`
                  : cache.progress.state === 'running'
                    ? `Filling: ${(cache.progress.held || 0).toLocaleString('en-GB')}`
                      + `${cache.progress.expected != null ? ` of ${cache.progress.expected.toLocaleString('en-GB')}` : ''} entries`
                      + `${cache.progress.percent != null ? ` (${cache.progress.percent}%)` : ''}, continuing by itself.`
                    : 'The full sync has not been started.'
              )}
            </div>
            {!sure && onRefreshCache && (
              <button onClick={onRefreshCache} disabled={busy}
                className="mt-1 px-2 py-0.5 text-[8px] font-bold rounded border border-amber-700 text-amber-900 bg-white hover:bg-amber-100 disabled:opacity-40">
                {busy ? 'Working…' : cache.progress?.state === 'running'
                  ? 'Run the next batch now and search again'
                  : 'Start filling our copy of Sage and search again'}
              </button>
            )}
          </div>
          <button onClick={onClose} className={`text-[9px] flex-shrink-0 ${sure ? 'text-gray-400 hover:text-gray-700' : 'text-amber-500 hover:text-amber-800'}`}>✕</button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-1.5 rounded border border-blue-300 bg-blue-50/60 px-2.5 py-2">
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div>
          <div className="text-[9px] font-bold text-blue-900">
            {/* An entry that matched nothing about the document is not a "possible
                match", it is something to look at. Counting the two together would
                overstate the answer. */}
            {(() => {
              const matches = found.candidates.filter(c => c.confidence !== 'supplier_date').length;
              const prompts = found.candidates.length - matches;
              const nEntries = (n) => `${n} entr${n === 1 ? 'y' : 'ies'}`;
              if (!matches) return `Nothing matched — ${nEntries(prompts)} against this supplier around this date`;
              return `${matches} possible match${matches === 1 ? '' : 'es'} already in Sage`
                + (prompts ? `, and ${nEntries(prompts)} against this supplier worth a look` : '');
            })()}
          </div>
          <div className="text-[8px] text-blue-700">
            Linking records that the ledger already has this one. Nothing is posted.
          </div>
        </div>
        <button onClick={onClose} className="text-[9px] text-blue-400 hover:text-blue-800 flex-shrink-0">✕</button>
      </div>

      {found.candidates.slice(0, 6).map(c => {
        const conf = CONF[c.confidence] || CONF.amount;
        // Same supplier or not is a fact worth stating plainly: a different name is
        // the normal case for an agency invoicing on somebody's behalf, and also
        // exactly what a wrong match looks like.
        const sameSupplier = !!(ours.supplier_name && c.contact_name)
          && ours.supplier_name.toLowerCase().replace(/[^a-z0-9]/g, '')
             .includes(c.contact_name.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 6));
        const rows = [
          {
            label: 'Invoice number',
            ours: ours.invoice_number || '—',
            theirs: c.matched?.invoice_number
              ? `${c.matched.invoice_number.value}`
              : (c.supplier_reference || c.reference || '—'),
            note: c.matched?.invoice_number
              ? `their ${c.matched.invoice_number.field.replace(/_/g, ' ')}`
              : null,
            ok: !!c.matched?.invoice_number,
          },
          {
            label: 'Amount',
            ours: money(ours.total_incl_vat),
            theirs: money(c.total_amount),
            note: c.outstanding_amount != null && c.outstanding_amount !== c.total_amount
              ? `${money(c.outstanding_amount)} outstanding` : null,
            ok: !!c.matched?.amount,
          },
          {
            label: 'Supplier',
            ours: ours.supplier_name || '—',
            theirs: c.contact_name || '—',
            note: !sameSupplier && c.contact_name ? 'different name in Sage' : null,
            ok: sameSupplier,
          },
          {
            label: 'Date',
            ours: ours.invoice_date || '—',
            theirs: c.date || '—',
            ok: !!(ours.invoice_date && c.date && String(ours.invoice_date).slice(0, 10) === String(c.date).slice(0, 10)),
          },
          ...(c.matched?.po_number ? [{
            label: 'Our PO',
            ours: ours.po_number || '—',
            theirs: c.matched.po_number.value,
            note: `their ${c.matched.po_number.field.replace(/_/g, ' ')}`,
            ok: true,
          }] : []),
        ];

        return (
          <div key={c.id} className="bg-white rounded border border-blue-200 mb-1.5 overflow-hidden">
            <div className={`flex items-center justify-between gap-2 px-2 py-1 border-b ${conf.cls}`}>
              <span className="text-[8px] font-bold">
                {conf.label}
                {/* What else agrees. Nothing reaches this list on one weak signal
                    alone, and saying which signal it was is the difference between a
                    suggestion and a guess. */}
                {c.confidence !== 'certain' && c.corroboration && (
                  <span className="font-normal opacity-80">
                    {' — also '}
                    {[
                      c.corroboration.amount ? 'the amount' : null,
                      c.corroboration.same_supplier ? 'the supplier'
                        : c.corroboration.same_contact ? 'the supplier’s own Sage contact' : null,
                      c.corroboration.days_apart != null && c.corroboration.days_apart <= 60
                        ? (c.corroboration.days_apart === 0 ? 'the same date' : `dated ${c.corroboration.days_apart}d apart`)
                        : null,
                    ].filter(Boolean).join(', ') || 'nothing else'}
                  </span>
                )}
              </span>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {c.status && <span className="text-[7px] opacity-80">{c.status}</span>}
                <button onClick={() => onLink(c)} disabled={busy}
                  className={`px-2 py-0.5 text-[8px] font-bold rounded whitespace-nowrap disabled:opacity-40 ${
                    c.confidence === 'certain' ? 'bg-navy text-white hover:bg-navy/80'
                      : 'border border-navy text-navy bg-white hover:bg-navy/5'}`}>
                  This is it — link
                </button>
              </div>
            </div>
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-2 py-0.5 text-left text-[7px] uppercase font-bold text-gray-400 w-[22%]" />
                  <th className="px-2 py-0.5 text-left text-[7px] uppercase font-bold text-gray-500">Ours</th>
                  <th className="px-2 py-0.5 text-left text-[7px] uppercase font-bold text-gray-500">In Sage</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.label} className="border-t border-gray-100">
                    <td className="px-2 py-0.5 text-[8px] font-semibold text-gray-500 whitespace-nowrap">{r.label}</td>
                    <td className="px-2 py-0.5 text-[8px] text-gray-800 font-mono">{r.ours}</td>
                    <td className={`px-2 py-0.5 text-[8px] font-mono ${r.ok ? 'text-emerald-700 font-semibold' : 'text-amber-800'}`}>
                      <span className="mr-1">{r.ok ? '✓' : '≠'}</span>{r.theirs}
                      {r.note && <span className="ml-1 font-sans text-[7px] text-gray-400">({r.note})</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

// ── Invoice list (within detail pane) ────────────────────────────────────────
function InvoiceList({ invoices, onEdit, onDelete, sageConnected, autoCheckFor = null, onAutoChecked, onChanged, portalEntry = false }) {
  const [historyFor, setHistoryFor] = useState(null);
  if (!invoices.length && portalEntry) {
    return (
      <div className="text-center py-6 text-[8px] text-gray-400">
        Nothing to post yet.<br />
        <span className="text-[7px]">The invoice is logged here for Sage by itself once it is approved on Ext. Resource Invoices.</span>
      </div>
    );
  }
  if (!invoices.length) {
    return (
      <div className="text-center py-6 text-[8px] text-gray-400">
        No invoices logged yet.<br />
        <span className="text-[7px]">Pick an attachment above and press Extract Invoice, or log one by hand</span>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {invoices.map(inv => (
        <div key={inv.id} className="bg-white border border-gray-200 rounded p-2.5 hover:border-gray-300">
          <div className="flex items-start gap-1.5">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="text-[9px] font-bold text-navy">{inv.invoice_number || 'No number'}</span>
                {inv.contractor_invoice_id && (
                  <span className="text-[7px] font-bold px-1 py-0.5 rounded bg-amber-50 text-amber-700"
                    title="Logged from Ext. Resource Invoices when the contractor invoice was approved">
                    🏗 Contractor invoice
                  </span>
                )}
                {/* Money back, not money owed. Without this a credit note reads
                    exactly like an invoice here and gets chased for payment. */}
                {inv.document_type === 'credit_note' && (
                  <span className="text-[7px] font-bold px-1 py-0.5 rounded bg-red-100 text-red-700">↩ Credit note</span>
                )}
                {/* This pill used to print the raw reporting flag, so a saved
                    invoice said "pending" — which reads as "queued for Sage"
                    and is not what that field means. Saving an invoice sends it
                    nowhere. The pill now says where the invoice actually is, and
                    the reporting flag is only mentioned when it is not the
                    ordinary one. */}
                {(() => {
                  const s = inv.sage_verified_at && !inv.sage_verify_issues
                      ? { label: 'In Sage · confirmed', cls: 'bg-emerald-100 text-emerald-700' }
                    : inv.sage_invoice_id
                      ? { label: 'In Sage', cls: 'bg-emerald-100 text-emerald-700' }
                    : inv.sage_posted_unidentified
                      ? { label: 'In Sage · not linked', cls: 'bg-amber-100 text-amber-800' }
                    : inv.autoentry_sent_at
                      ? { label: 'Sent to AutoEntry', cls: 'bg-indigo-100 text-indigo-700' }
                    // A decision, with a name and a reason against it — not the
                    // same thing as an invoice nobody has got round to.
                    : inv.sage_excluded_reason
                      ? { label: 'Excluded from Sage', cls: 'bg-gray-200 text-gray-700' }
                      : { label: 'NOT IN SAGE — outstanding', cls: 'bg-red-100 text-red-700' };
                  return <span className={`text-[7px] font-bold px-1 py-0.5 rounded ${s.cls}`}>{s.label}</span>;
                })()}
                {inv.sage_excluded_reason && (
                  <span className="text-[7px] text-gray-500 italic truncate max-w-[14rem]" title={inv.sage_excluded_reason}>
                    {inv.sage_excluded_reason}
                  </span>
                )}
                {inv.status === 'rejected' && (
                  <span className="text-[7px] font-bold px-1 py-0.5 rounded bg-red-100 text-red-600">Excluded from reports</span>
                )}
                {inv.status === 'matched' && (
                  <span className="text-[7px] px-1 py-0.5 rounded bg-gray-100 text-gray-500">Checked off</span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-1 text-[8px]">
                {inv.invoice_date   && <span className="text-gray-500">📅 {formatDate(inv.invoice_date)}</span>}
                {inv.supplier_name  && <span className="text-gray-500 truncate">🏢 {inv.supplier_name}</span>}
                {inv.total_incl_vat != null && <span className="font-semibold text-navy col-span-2">{formatMoney(inv.total_incl_vat, 2, inv.currency)} incl. VAT</span>}
                {inv.po_number      && <span className="text-blue-600">PO: {inv.po_number}</span>}
                {inv.sow_reference  && <span className="text-purple-600">SOW: {inv.sow_reference}</span>}
              </div>
              {inv.notes && <div className="text-[7px] text-gray-400 mt-0.5 truncate">{inv.notes}</div>}
              {sageConnected ? (
                <SagePanel invoice={inv} onChanged={onChanged} onEdit={onEdit}
                  autoCheck={autoCheckFor === inv.id} onAutoChecked={onAutoChecked} />
              ) : !isSettled(inv) && (
                // The Post button lives in the Sage panel, which needs a
                // connection. Without one, say so here rather than leave an
                // invoice with no way forward and no reason given.
                <div className="mt-1.5 px-2 py-1 rounded text-[7px] bg-red-50 border border-red-200 text-red-700">
                  Sage is not connected, so this cannot be posted from here.{' '}
                  <a href="#sage" className="underline font-semibold">Connect Sage on the Sage screen →</a>
                </div>
              )}
            </div>
            <div className="flex gap-0.5 flex-shrink-0">
              <button onClick={() => setHistoryFor(inv.id)} className="text-[7px] text-gray-400 hover:text-navy px-1 py-0.5 rounded hover:bg-gray-100" title="Who did what, and when">History</button>
              {/* Edit is the fix for almost everything that stops an invoice
                  reaching Sage, so on an invoice that is not there yet it stops
                  looking like a grey afterthought next to History. */}
              <button onClick={() => onEdit(inv)}
                className={`text-[7px] px-1 py-0.5 rounded ${
                  !inv.sage_invoice_id && !inv.autoentry_sent_at && !inv.sage_posted_unidentified
                    ? 'font-bold text-navy border border-navy/30 hover:bg-navy/5'
                    : 'text-gray-400 hover:text-navy hover:bg-gray-100'}`}>
                Edit
              </button>
              {/* A contractor invoice's is taken off on Ext. Resource Invoices
                  (reject it there); deleting it here would only bring it back. */}
              {!inv.contractor_invoice_id && (
                <button onClick={() => onDelete(inv.id)} className="text-[7px] text-gray-400 hover:text-red-500 px-1 py-0.5 rounded hover:bg-red-50">✕</button>
              )}
            </div>
          </div>
        </div>
      ))}
      {historyFor && <InvoiceHistory invoiceId={historyFor} onClose={() => { setHistoryFor(null); onChanged?.(); }} />}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
const AUTO_SYNC_INTERVAL = 5 * 60 * 1000; // 5 minutes
// The Sage connection is re-checked more often than the mailbox is synced: it is a
// cheap read, and a stale answer here is the difference between "post this" and
// "nothing can be posted".
const SAGE_STATUS_INTERVAL = 60 * 1000;

// initialEmailId arrives from a deep link — a search result for a supplier
// invoice, or the ops link written into the Sage entry. The email is opened
// straight away and the list is widened to "All", because the email an invoice
// was logged from has usually been processed and would not be in the default
// "Unprocessed" view for the row to be found in.
export default function EmailInboxPage({ initialEmailId = null }) {
  const [emails,             setEmails]             = useState([]);
  const [suppliers,          setSuppliers]          = useState([]);
  const [sows,               setSows]               = useState([]);
  const [pos,                setPos]                = useState([]);
  const [overheadCategories, setOverheadCategories] = useState([]);
  const [resourceWorkers,    setResourceWorkers]    = useState([]);
  const [loading,            setLoading]            = useState(true);
  const [syncing,      setSyncing]      = useState(false);
  const [syncMsg,      setSyncMsg]      = useState('');
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [statusFilter, setStatusFilter] = useState(initialEmailId ? 'all' : 'unprocessed');
  const [counts,       setCounts]       = useState({});
  const [total,        setTotal]        = useState(0);
  const [sageRef,       setSageRef]       = useState(null);
  // Refetched after a supplier default changes, so the form shows the new usual
  // code without a page reload.
  const loadSageRef = useCallback(() => api.get('sage/reference').then(setSageRef).catch(() => {}), []);

  // THE SAGE CONNECTION, RE-CHECKED RATHER THAN REMEMBERED.
  //
  // The status was read once when the page mounted and never again, so the
  // disconnect banner could sit there long after somebody had reconnected — you
  // press Reconnect, the Sage screen says connected, and coming back here it still
  // claims Sage is down. Equally, a connection that died while the tab was open
  // said nothing until a post failed.
  //
  // Re-checked on a timer and whenever the tab regains focus, which is when
  // somebody has just come back from reconnecting.
  const loadSageStatus = useCallback(() => api.get('sage/status').then(st => {
    setSageConnected(!!st?.connected);
    setSagePostingEnabled(!!st?.posting_enabled);
    setAutoentryEnabled(!!st?.autoentry_forward_enabled);
    setSageReauthRequired(!!st?.reauth_required);
    if (st?.connected) loadSageRef();
  }).catch(() => {}), [loadSageRef]);
  const [sageConnected, setSageConnected] = useState(false);
  const [sageReauthRequired, setSageReauthRequired] = useState(false);
  // Invoices logged here that the ledger has not accepted. The number that has to
  // reach zero, counted server-side across every email rather than the page shown.
  const [awaitingSage, setAwaitingSage] = useState(0);
  const [excludedFromSage, setExcludedFromSage] = useState(0);
  // Contractor invoices still with finance: waiting on approval (and since
  // when, for chasing) and approved but not yet in Sage.
  const [contractor, setContractor] = useState({ awaiting_approval: 0, oldest_awaiting_since: null, ready_for_sage: 0 });
  // Where posting to Sage starts, and what that has already dealt with, so the
  // headline can distinguish a real backlog from history somebody has drawn a line
  // under.
  const [cutover, setCutover] = useState(null);
  const [settingCutover, setSettingCutover] = useState(false);
  // Whether Sage will actually accept a post. Off until the server says
  // otherwise, so the combined "log and check" action never appears in front of
  // a switch that is off and produce a Post button that refuses.
  const [sagePostingEnabled, setSagePostingEnabled] = useState(false);
  // Off unless the server says otherwise, so the button cannot appear during the
  // moment before status loads and be pressed into a refusal.
  const [autoentryEnabled, setAutoentryEnabled] = useState(false);
  const [selected,     setSelected]     = useState(initialEmailId);
  // The deep link currently being honoured. Held in a ref so the tab-change
  // effect below can tell "the user switched tabs" (clear the selection) from
  // "a deep link switched the tab to find its email" (keep it).
  const deepLinkRef                     = useRef(initialEmailId);
  const syncMsgTimerRef                 = useRef(null);
  const [, setTick]                     = useState(0); // for timeAgo re-renders

  // Tick every 30s so "X min ago" updates
  useEffect(() => {
    const t = setInterval(() => setTick(n => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  // Filtering happens server-side. The list is capped at 200 rows, so filtering
  // in the browser meant "unprocessed" showed the unprocessed emails inside the
  // most recent 200 rather than all of them — which is the opposite of what a
  // default view of outstanding work should do. Counts come back separately and
  // cover the whole table.
  const fetchEmails = useCallback((status = statusFilter) =>
    api.get('procurement/emails', status === 'all' ? {} : { status })
      .then(d => {
        setEmails(d?.emails || []); setCounts(d?.counts || {}); setTotal(d?.total || 0);
        setAwaitingSage(d?.awaiting_sage || 0); setExcludedFromSage(d?.excluded_from_sage || 0);
        setCutover(d?.sage_cutover || null);
        if (d?.contractor) setContractor(d.contractor);
      })
      .catch(() => {}),
  [statusFilter]);

  // ── "We agreed the cutover yesterday, so this is confusing" ────────────────
  //
  // Offered here because here is where the confusing number is. Invoices dated
  // before yesterday are recorded as deliberately not going to Sage, with the
  // reason, and the emails whose every invoice is then settled are checked off so
  // the tabs stop asking for them too. Nothing is deleted, everything stays
  // searchable and linkable, and it can be lifted on the Sage screen.
  const applyCutover = async () => {
    const from = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
    if (!window.confirm(
      `Stop asking for invoices dated before ${from}?\n\n`
      + 'They are already in Sage from the old process, so they are recorded as deliberately not going there, with '
      + 'the reason. Nothing is deleted: they stay visible, stay searchable, and any one of them can still be linked '
      + 'to its Sage entry or put back on the list.')) return;
    setSettingCutover(true);
    try {
      const r = await api.post('procurement/supplier-invoices/sage-cutover', { from_date: from });
      setSyncMsg(`✓ ${r.excluded} set aside before ${r.from_date}`
        + `${r.emails_settled ? `, ${r.emails_settled} email${r.emails_settled === 1 ? '' : 's'} checked off` : ''}`);
      setTimeout(() => setSyncMsg(''), 6000);
      await fetchEmails(statusFilter);
    } catch (e) {
      setSyncMsg(`✗ ${e.message || 'Could not set the cutover'}`);
    } finally { setSettingCutover(false); }
  };

  // Initial load
  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get('procurement/emails', statusFilter === 'all' ? {} : { status: statusFilter }),
      api.get('suppliers'),
      api.get('sows', { status: 'Active' }),
      api.get('purchase-orders'),
      api.get('invoice-overhead-categories'),
    ]).then(([inbox, suppliers, sows, pos, overheadCats]) => {
      setEmails(inbox?.emails || []);
      setCounts(inbox?.counts || {});
      setTotal(inbox?.total || 0);
      setAwaitingSage(inbox?.awaiting_sage || 0);
      setExcludedFromSage(inbox?.excluded_from_sage || 0);
      setCutover(inbox?.sage_cutover || null);
      if (inbox?.contractor) setContractor(inbox.contractor);
      setSuppliers(suppliers || []);
      setSows(sows || []);
      setPos(pos || []);
      setOverheadCategories(overheadCats || []);
      setLoading(false);
    }).catch(() => setLoading(false));
    // Load active resource workers for contractor matching (best-effort — doesn't block page)
    api.get('resource-workers/active').then(r => setResourceWorkers(r || [])).catch(() => {});
    // Sage is optional and finance-only. Both calls are best-effort: someone
    // without the finance role still gets a fully working inbox, just without
    // the ledger picker and the post button.
    loadSageStatus();
  }, []);

  // A deep link that arrives while the page is already open (searching for an
  // ops reference without leaving the inbox first).
  useEffect(() => {
    if (!initialEmailId || initialEmailId === deepLinkRef.current) return;
    deepLinkRef.current = initialEmailId;
    setSelected(initialEmailId);
    setStatusFilter('all');
  }, [initialEmailId]);

  // Changing tab refetches rather than re-slicing what is already loaded.
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    // Cleared because the email being read is rarely in the tab just switched
    // to — unless a deep link is what switched it, in which case that email is
    // the whole reason for the change.
    setSelected(s => (s && s === deepLinkRef.current ? s : null));
    fetchEmails(statusFilter);
  }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Background auto-sync ──────────────────────────────────────────────────
  const silentSync = useCallback(async () => {
    if (syncing) return;
    try {
      const res = await api.post('procurement/emails/sync', {});
      setLastSyncedAt(new Date().toISOString());
      if (!res.setup_required && res.synced > 0) {
        // Only update list if new emails arrived — avoids scroll jumps
        await fetchEmails();
        setSyncMsg(`✓ ${res.synced} new email${res.synced !== 1 ? 's' : ''}`);
        clearTimeout(syncMsgTimerRef.current);
        syncMsgTimerRef.current = setTimeout(() => setSyncMsg(''), 4000);
      }
    } catch { /* silent */ }
  }, [syncing, fetchEmails]);

  useEffect(() => {
    const t = setInterval(silentSync, AUTO_SYNC_INTERVAL);
    return () => clearInterval(t);
  }, [silentSync]);

  // Keep the Sage connection state current: on a timer, and on returning to the
  // tab — which is exactly the moment somebody comes back from reconnecting and
  // would otherwise still be looking at a banner saying Sage is down.
  useEffect(() => {
    const t = setInterval(loadSageStatus, SAGE_STATUS_INTERVAL);
    const onFocus = () => loadSageStatus();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      clearInterval(t);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [loadSageStatus]);

  // ── Bulk clear-down ───────────────────────────────────────────────────────
  // For the fresh start after moving the watched folder. Emails that produced an
  // invoice are held back and reported rather than swept up, because those are
  // real work and burying them is how something that mattered goes quiet.
  // ── Manual sync ───────────────────────────────────────────────────────────
  const manualSync = async () => {
    setSyncing(true); setSyncMsg('');
    try {
      const res = await api.post('procurement/emails/sync', {});
      setLastSyncedAt(new Date().toISOString());
      if (res.setup_required) {
        setSyncMsg(`⚠ Setup required: ${res.error}`);
      } else {
        setSyncMsg(`✓ Synced ${res.synced} new email${res.synced !== 1 ? 's' : ''} (checked ${res.total_checked})`);
        await fetchEmails();
      }
    } catch (e) {
      setSyncMsg(`✗ ${e.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const onStatusChange = (emailId, newStatus) => {
    setEmails(prev => prev.map(e => e.id === emailId ? { ...e, status: newStatus } : e));
    // Keep the tab counts honest without a round trip. The email stays visible
    // until the list is next fetched, so processing one does not make the panel
    // vanish underneath whoever is working on it.
    setCounts(prev => {
      const current = emails.find(e => e.id === emailId)?.status;
      if (!current || current === newStatus) return prev;
      return { ...prev, [current]: Math.max(0, (prev[current] || 0) - 1), [newStatus]: (prev[newStatus] || 0) + 1 };
    });
  };

  const filtered = emails;

  return (
    <div className="fade-in flex flex-col h-full" style={{ height: 'calc(100vh - 56px)' }}>
      {/* ── Top bar ── */}
      <div className="flex items-center justify-between px-1 pb-3 flex-shrink-0 flex-wrap gap-2">
        <div>
          <h2 className="text-base font-extrabold text-navy">Accounts Email Inbox</h2>
          <p className="text-[8px] text-gray-400 mt-0.5">
            Process supplier emails · extract invoices · match to POs and SOWs
          </p>
        </div>
        <div className="flex items-center gap-2">
          {syncMsg && (
            <span className={`text-[7px] px-2 py-1 rounded border ${
              syncMsg.startsWith('✓') ? 'bg-emerald-50 border-emerald-200 text-emerald-700' :
              syncMsg.startsWith('⚠') ? 'bg-amber-50 border-amber-200 text-amber-700' :
              'bg-red-50 border-red-200 text-red-600'}`}>{syncMsg}</span>
          )}
          {lastSyncedAt && (
            <span className="text-[7px] text-gray-300">Last sync: {timeAgo(lastSyncedAt)}</span>
          )}
          <button onClick={manualSync} disabled={syncing}
            className="px-3 py-1.5 text-[8px] font-bold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-50 flex items-center gap-1">
            {syncing
              ? <><span className="animate-spin w-2.5 h-2.5 border border-white border-t-transparent rounded-full" />Syncing…</>
              : '⟳ Sync Now'}
          </button>
        </div>
      </div>

      {/* Sage has rejected the stored authorisation. Every duplicate check and
          every post fails until it is reconnected, so it is said once, here,
          rather than found out invoice by invoice — and with the link, because
          the fix is on another screen. */}
      {sageReauthRequired && (
        <div className="mb-3 flex items-start justify-between gap-3 flex-wrap rounded border border-red-300 bg-red-50 px-3 py-2 flex-shrink-0">
          <div>
            <div className="text-[9px] font-bold text-red-900">Sage has disconnected — nothing can be posted or checked</div>
            <div className="text-[8px] text-red-800 mt-0.5">
              Sage rejected the stored authorisation. Invoices can still be logged here, but they cannot reach the
              ledger and the duplicate check cannot run, so posting is held rather than risked.
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {/* Re-check by hand, for the case where somebody has just reconnected
                in another tab and does not want to wait for the next poll. */}
            <button onClick={loadSageStatus}
              className="px-2 py-1 text-[8px] font-semibold border border-red-300 text-red-800 rounded bg-white hover:bg-red-100 whitespace-nowrap">
              Check again
            </button>
            <a href="#sage" className="px-2.5 py-1 text-[8px] font-bold bg-navy text-white rounded hover:bg-navy/80 whitespace-nowrap">
              Reconnect Sage →
            </a>
          </div>
        </div>
      )}

      {/* ── Summary tiles ──
          The four email counts said nothing about the ledger, so an inbox could
          read as fully processed with a stack of invoices that had never reached
          Sage. "Not in Sage" is the fifth tile and the one that has to reach zero;
          it counts INVOICES rather than emails, which is why it is a different
          colour and sits apart. */}
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 mb-3 flex-shrink-0">
        {[
          ['All',              total,                        'text-navy'],
          ['Unprocessed',      counts.unprocessed     || 0,  'text-blue-500'],
          ['Invoice Received', counts.invoice_received || 0, 'text-amber-500'],
          ['Matched',          counts.matched         || 0,  'text-emerald-500'],
        ].map(([label, count, cls]) => (
          <div key={label} className="bg-white border border-gray-200 rounded px-3 py-2">
            <div className="text-[7px] text-gray-400 uppercase font-bold mb-0.5">{label}</div>
            <div className={`text-lg font-extrabold ${cls}`}>{count}</div>
          </div>
        ))}
        {/* NOT IN SAGE — and what a cutover has already dealt with.
            A bare 238 read as "nothing happened" the day after a cutover was
            agreed. The count was right (it always skipped excluded invoices) but a
            number on its own cannot say whether a line has been drawn, so there
            was no way to tell "238 still to do" from "the cutover is not being
            honoured". The state is shown with the number now. */}
        <div className={`rounded px-3 py-2 border ${awaitingSage > 0 ? 'bg-red-50 border-red-300' : 'bg-white border-emerald-200'}`}>
          <div className={`text-[7px] uppercase font-bold mb-0.5 ${awaitingSage > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
            Not in Sage
          </div>
          <div className={`text-lg font-extrabold ${awaitingSage > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{awaitingSage}</div>
          <div className="text-[6px] text-gray-400 leading-tight">
            {awaitingSage > 0 ? 'logged invoices not yet accepted' : 'everything logged is in the ledger'}
            {cutover?.from_date
              ? <> · <span className="text-gray-500">since {cutover.from_date}</span></>
              : excludedFromSage > 0 ? ` · ${excludedFromSage} excluded` : ''}
          </div>
          {cutover?.from_date ? (
            <div className="text-[6px] text-gray-400 leading-tight mt-0.5">
              {cutover.set_aside > 0 && <>{cutover.set_aside} earlier one{cutover.set_aside === 1 ? '' : 's'} set aside. </>}
              {/* Outstanding AND older than the cutover: the tell that the line was
                  not applied to everything it covers. Named rather than absorbed
                  into the total, because it is the only way to notice. */}
              {cutover.older_still_outstanding > 0 && (
                <span className="text-amber-700 font-semibold">
                  {cutover.older_still_outstanding} older than the cutover are still on the list — re-apply it on the Sage screen.
                </span>
              )}
            </div>
          ) : awaitingSage > 20 && (
            <div className="text-[6px] text-gray-400 leading-tight mt-0.5">
              No cutover set, so this includes everything ever logged.{' '}
              {/* Offered where the confusing number is, not on another screen. The
                  invoices are set aside with a reason, stay visible and stay
                  linkable — nothing is deleted and nothing is pretended. */}
              <button onClick={applyCutover} disabled={settingCutover}
                className="underline text-navy font-semibold disabled:opacity-40">
                {settingCutover ? 'Working…' : 'Start from yesterday'}
              </button>
            </div>
          )}
        </div>
        {/* CONTRACTOR INVOICES — the ones still with finance. Waiting on approval
            is the chase list, so the oldest wait is shown with it; approved
            ones are also in "Not in Sage" until they are posted. Pressing it
            opens the Contractor tab. */}
        {(() => {
          const oldest = daysSince(contractor.oldest_awaiting_since);
          const overdue = oldest != null && oldest >= CHASE_AFTER_DAYS;
          const open = (contractor.awaiting_approval || 0) + (contractor.ready_for_sage || 0);
          return (
            <button onClick={() => setStatusFilter('contractor')}
              className={`text-left rounded px-3 py-2 border ${overdue ? 'bg-red-50 border-red-300' : open ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-200'}`}>
              <div className="text-[7px] uppercase font-bold mb-0.5 text-amber-700">🏗 Contractor invoices</div>
              <div className="text-lg font-extrabold text-navy">{contractor.awaiting_approval || 0}
                <span className="text-[8px] font-semibold text-gray-500"> awaiting approval</span>
              </div>
              <div className="text-[6px] text-gray-500 leading-tight">
                {oldest != null && <span className={overdue ? 'text-red-700 font-semibold' : ''}>oldest waiting {daysLabel(oldest)}</span>}
                {oldest != null && contractor.ready_for_sage > 0 && ' · '}
                {contractor.ready_for_sage > 0 && <>{contractor.ready_for_sage} approved, to post</>}
                {!open && 'none waiting'}
              </div>
            </button>
          );
        })()}
      </div>

      {/* ── Filter tabs ── */}
      <div className="flex border-b border-gray-200 mb-2 flex-shrink-0">
        {[
          ['all',              `All (${total})`],
          ['unprocessed',      `Unprocessed (${counts.unprocessed || 0})`],
          ['invoice_received', `Invoice (${counts.invoice_received || 0})`],
          ['matched',          `Matched (${counts.matched || 0})`],
          ['not_applicable',   `N/A (${counts.not_applicable || 0})`],
          // Contractor invoices finance still have to deal with, wherever their
          // entry sits in the tabs above.
          ['contractor',       `🏗 Contractor (${(contractor.awaiting_approval || 0) + (contractor.ready_for_sage || 0)})`],
        ].map(([key, label]) => (
          <button key={key} onClick={() => setStatusFilter(key)}
            className={`px-3 py-1.5 text-[8px] font-medium border-b-2 -mb-px mr-1 whitespace-nowrap ${
              statusFilter === key ? 'border-gold text-gold font-semibold' : 'border-transparent text-gray-400 hover:text-gray-600'
            }`}>
            {label}
          </button>
        ))}
      </div>

      {/* ── Body: two-column when email selected ── */}
      <div className="flex flex-1 min-h-0 gap-0 overflow-hidden">
        {/* Email list */}
        <div className={`flex flex-col overflow-hidden flex-shrink-0 ${selected ? 'border-r border-gray-100' : 'w-full'}`}
          style={selected ? { width: 300 } : {}}>
          {loading ? (
            <div className="text-center py-12 text-[9px] text-gray-400">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded p-8 text-center m-1">
              <div className="text-[8px] text-gray-400">
                {statusFilter === 'all'
                  ? 'No emails synced yet. Configure mailbox in Admin → Integrations, then sync.'
                  : statusFilter === 'contractor'
                    ? 'No contractor invoices waiting on approval or on Sage.'
                    : `No ${statusFilter.replace('_', ' ')} emails.`}
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto space-y-1 py-0.5 pr-1">
              {filtered.map(email => {
                const cfg = STATUS_CONFIG[email.status] || {};
                return (
                  <div key={email.id}
                    onClick={() => setSelected(email.id)}
                    className={`bg-white border rounded-lg px-3 py-2.5 cursor-pointer hover:shadow-sm transition-all ${
                      selected === email.id ? 'border-navy shadow-sm' : 'border-gray-200 hover:border-gray-300'
                    }`}>
                    <div className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5"
                        style={{ background: cfg.dot || '#D1D5DB' }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-1 mb-0.5">
                          <span className={`text-[9px] leading-tight truncate ${email.status === 'unprocessed' ? 'font-bold text-navy' : 'font-medium text-gray-600'}`}>
                            {email.subject}
                          </span>
                          <span className="text-[7px] text-gray-300 flex-shrink-0 text-right leading-tight">
                            {formatDate(email.received_at)}
                            <span className="block text-gray-400">{formatTime(email.received_at)}</span>
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-[7px] text-gray-400">
                          <span className="truncate">{email.sender_name || email.sender_email}</span>
                          {!!email.has_attachments && <span className="text-blue-400 flex-shrink-0">📎{email.attachment_count > 1 ? email.attachment_count : ''}</span>}
                          {email.invoice_count > 0 && <span className="text-amber-500 flex-shrink-0">📄{email.invoice_count}</span>}
                          {email.contractor_invoice_id ? (() => {
                            // A contractor invoice: where it has got to, and on
                            // an unapproved one how long it has waited.
                            const st = CONTRACTOR_STAGE[email.contractor_stage] || CONTRACTOR_STAGE.awaiting_approval;
                            const waited = email.contractor_stage === 'awaiting_approval' ? daysSince(email.contractor_submitted_at) : null;
                            const overdue = waited != null && waited >= CHASE_AFTER_DAYS;
                            return (
                              <span className={`text-[7px] font-semibold px-1.5 py-0.5 rounded border flex-shrink-0 whitespace-nowrap ${overdue ? 'bg-red-50 text-red-700 border-red-200' : st.cls}`}>
                                🏗 {st.label}{waited != null ? ` · ${daysLabel(waited)}` : ''}
                              </span>
                            );
                          })() : resourceWorkers.some(w => w.email && w.email.toLowerCase() === (email.sender_email || '').toLowerCase()) && (
                            <span className="text-[8px] bg-amber-100 text-amber-700 font-semibold px-1.5 py-0.5 rounded border border-amber-200 flex-shrink-0">🏗 Contractor</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Email detail */}
        {selected && (
          <div className="flex-1 min-w-0 overflow-hidden">
            <EmailDetail
              key={selected}
              emailId={selected}
              suppliers={suppliers}
              sows={sows}
              pos={pos}
              overheadCategories={overheadCategories}
              sageRef={sageRef}
              sageConnected={sageConnected}
              sagePostingEnabled={sagePostingEnabled}
              autoentryEnabled={autoentryEnabled}
              onSageRefresh={loadSageRef}
              resourceWorkers={resourceWorkers}
              onStatusChange={onStatusChange}
              onSupplierCreated={s => setSuppliers(prev => [...prev, s])}
              onClose={() => setSelected(null)}
            />
          </div>
        )}

        {/* Placeholder when nothing selected */}
        {!selected && !loading && filtered.length > 0 && (
          <div className="flex-1 flex items-center justify-center text-[9px] text-gray-300 border-l border-gray-100">
            ← Select an email to process
          </div>
        )}
      </div>
    </div>
  );
}
